import {
    BuyerReferenceType,
    CampaignErrorableType,
    CampaignErrorType,
    CampaignStatus,
    Displays_LineItemLineItemType,
    EsPCAReportField,
    EsPCAReportFilterField,
    PCAReportField,
    PCAReportMetric,
    UserType,
} from 'c-sdk';
import { ChartLabelAnchor } from 'c-reports/Components/ReportControlsWrapper/types';

const en = {
    CommonWords: {
        Impacts: 'Impacts',
        impacts: 'impacts',
        Pack: 'Pack',
        pack: 'pack',
        Frame: 'Frame',
        frame: 'frame',
        TVRegion: 'TV Region',
        TVRegions: 'TV Regions',
        Region: 'Region',
        Conurbation: 'Conurbation',
        Conurbations: 'Conurbations',
        Town: 'Town',
        Towns: 'Towns',
        MediaOwner: 'Media Owner',
        MediaOwners: 'Media Owners',
        mediaOwners: 'media owners',
        owner: 'owner',
        owners: 'owners',
        Owners: 'Owners',
        Owner: 'Owner',
        Add: 'Add',
        Cancel: 'Cancel',
        Save: 'Save',
    },
    Navigation: {
        dashboardModuleLabel: 'Dashboard',
        logoutLabel: 'Logout',
        darkModeLabel: 'Dark Mode',
        switchToDarkModeLabel: 'Switch to dark mode',
        switchToLightModeLabel: 'Switch to light mode',
        campaigns: 'Campaigns',
        agencies: 'Creative Agencies',
        buyers: 'Teams',
        clients: 'Brands',
        owners: '$t(CommonWords.Owners)',
        creatives: 'Creatives',
        dataStatus: 'Data Status',
        dataStatusLineItems: 'Data Status Line Items',
        admin: 'Admin',
        adminSkins: 'Skins',
        adminDashboardSetup: 'Dashboard Setup',
        adminSummarySetup: 'Summary Setup',
        adminUsers: 'Users',
        adminRoles: 'User Roles',
        adminMarkets: 'Markets',
        adminImpersonateUser: 'Impersonate User',
        adminUsage: 'Platform usage',
        adminStopImpersonateUser: 'Stop Impersonating',
        adminFrameSearch: '$t(CommonWords.Frame) Search',
        adminNotifications: 'Notifications',

        popDisplaysAdmin: 'Pop Displays',
        popDisplaysTaskQueue: 'Task Queue',
        popDisplaysLogFileImports: 'Log Imports',
        popDisplaysLogImportSource: 'Log Import Sources',
        popDisplayPlayoutExport: 'Playout Export',
        popDisplaysChannelFrames: 'Channel Explorer',
        popDisplaysVirtualCampaignBuilder: 'Campaign Builder',
        popDisplaysScheduleTemplates: 'Schedule Templates',

        displaysAdmin: 'Admin Displays',
        displaysDisplayList: 'Displays',
        displaysOwnerList: '$t(CommonWords.Owners)',
        displaysPacks: '$t(CommonWords.Pack)s',
        displaysAnalogue: 'Analogues',
        displaysTag: 'Tag',
        displaysCountry: 'Countries',
        displaysMarket: 'Markets',
        breadcrumbsLabel: 'Breadcrumb',
    },
    Pages: {
        DashboardHome: {
            metaTitle: 'Dashboard',
            metaTitle1: 'Dashboard Sub 1',
            metaTitle2: 'Dashboard Sub 2',
            metaTitleExample: 'Another Page',
            liveCampaigns: 'Number of Live Campaigns',
            campaignsByBrand: 'Campaigns by Brand',
            vendorSplits: '$t(CommonWords.MediaOwners) Split (Target $t(CommonWords.Impacts))',
            campaignPerformance: 'Campaign Performance (% $t(CommonWords.Impacts))',
            vendorPerformance:
                '$t(CommonWords.MediaOwners)/Campaign Performance (% $t(CommonWords.Impacts) Below 100%)',
            environmentSplits: 'Environment Splits (Target $t(CommonWords.Impacts))',
            Campaign: 'Campaign',
            impactsPercentage: '$t(CommonWords.Impacts) Percentage',
            totalImpacts: 'Total $t(CommonWords.Impacts)',
            inSchedule: 'In Schedule',
            outSchedule: 'Out Of Schedule',
            unbooked: 'Unbooked',
            expectedImpacts: 'Expected $t(CommonWords.Impacts)',
            widgetTitle: {
                top: 'Top',
                bottom: 'Bottom',
                by: 'by',
                impactsDelivery: '$t(CommonWords.Impacts) Delivery',
                allImpacts: 'All $t(CommonWords.Impacts)',
                additional: 'Additional $t(CommonWords.Impacts)',
                performance: '% $t(CommonWords.Impacts)',
                expectedVsActual: 'Expected vs Actual',
                auditedVsActual: 'Audited vs Actual',
                dim_display_owner: '$t(CommonWords.MediaOwners)',
                dim_display_env: 'Environments',
                dim_campaign_id: 'Campaigns',
                dim_display_format: 'Formats',
                dim_display_town: '$t(CommonWords.Towns)',
                dim_display_tv_region: '$t(CommonWords.TVRegions)',
                dim_display_type: 'Types',
                dim_display_conurbation: '$t(CommonWords.Conurbations)',
            },
            fields: {
                id: 'ID',
                name: 'Name',
                reference: 'Reference',
                buyer: 'Buyer',
                agency: 'Agency',
                client: 'Client',
                frame_id: '$t(CommonWords.Frame) ID',
                campaign_id: 'Campaign',
                environment: 'Environment',
                creative_name: 'Creative',
                playout_start_date: 'Playout Start Date',
                playout_end_date: 'Playout End Date',
                day: 'Day',
                hour: 'Hour',
                expected_impacts_in_schedule: 'Expected $t(CommonWords.Impacts) (In Schedule)',
                impacts_received_in_schedule: '$t(CommonWords.Impacts) Received (In Schedule)',
                impacts_received_out_of_schedule:
                    '$t(CommonWords.Impacts) Received (Out Of Schedule)',
                impacts_received_unbooked: '$t(CommonWords.Impacts) Received (Unbooked)',
                geopath_audience: 'Audited $t(CommonWords.Impacts)',
                expected_plays_in_schedule: 'Expected Plays (In Schedule)',
                plays_received_in_schedule: 'Plays Received (In Schedule)',
                plays_received_out_of_schedule: 'Plays Received (Out Of Schedule)',
                plays_received_unbooked: 'Plays Received (Unbooked)',
                expected_time_in_schedule: 'Expected Time (In Schedule)',
                time_received_in_schedule: 'Time Received (In Schedule)',
                time_received_out_of_schedule: 'Time Received (Out Of Schedule)',
                time_received_unbooked: 'Time Received (Unbooked)',
                expected_plays: 'Expected Plays',
                sum_of_play: 'Plays',
                sum_of_time: 'Time',
                expected_time: 'Expected Time',
                expected_audience: 'Expected Audience',
                audience_received: 'Audience Received',
                audience_received_percent: 'Audience Received %',
                plays_with_verification: 'Plays With Verification',
                time_with_verification: 'Time With Verification',
                impacts_with_verification: '$t(CommonWords.Impacts) With Verification',
                energy_uses_kwh: 'Energy Used (kWh)',
                carbon_emission_kgs_co2e: 'Carbon Emission (kg CO2e)',
                display_owner: '$t(CommonWords.MediaOwner)',
                display_owner_id: '$t(CommonWords.MediaOwner) ID',
                display_env: 'Environment',
                display_format: 'Format',
                display_type: 'Type',
                display_town: '$t(CommonWords.Town)',
                display_tv_region: '$t(CommonWords.TVRegion)',
                display_conurbation: '$t(CommonWords.Conurbation)',
                display_resolution: 'Resolution',
                display_line_item_name: 'Line Item',
                display_screen_name_summary: 'Screen Name',
                display_surface_area: 'Surface Area',
            },
            expectedImpactsInSchedule: 'expected $t(CommonWords.Impacts) in Schedule',
            percentage: 'Percentage',
            custom: {
                saveAndApply: 'Save and apply',
                saveAndApplyNeedsName:
                    'Give the dashboard a name in "Save as…" to save it, along with any data source changes, and apply it.',
                saveAndApplyFailed: "Couldn't save the dashboard. Nothing was applied; try again.",
                datasourceIncomplete:
                    'Fill in the name, SQL query and schedule to save this data source.',
                total: 'Total',
                totals: 'Totals row',
                totalsHelp:
                    'Close the table with a row adding everything up. Calculated fields are worked out on the totals, so a ratio stays a ratio.',
                rowsPerPage: 'Rows per page',
                displayedRows: '{{from}}–{{to}} of {{count}}',
                openCampaign: 'Open campaign',
                openingCampaign: 'Opening campaign',
                campaignNotFound: "Couldn't find that campaign. It may have been deleted.",
                metricWithAggregation: '{{field}} ({{aggregation}})',
                aggregations: {
                    sum: 'Sum',
                    avg: 'Average',
                    min: 'Min',
                    max: 'Max',
                    count: 'Count',
                },
                missingCalc: 'Missing calculated field',
                download: 'Download',
                downloadPng: 'Image (PNG)',
                downloadPdf: 'PDF',
                downloadCsv: 'Data (CSV)',
                downloadAllCsv: 'All widget data (CSV)',
                downloadDashboard: 'Download',
                viewChart: 'Chart',
                viewTable: 'Table',
                layout: {
                    edit: 'Edit layout',
                    done: 'Done',
                    dragHandle: 'Drag to move',
                },
                stat: {
                    style: 'Style',
                    styles: {
                        number: 'Number',
                        ring: 'Ring to 100%',
                    },
                    size: 'Size',
                    sizes: {
                        small: 'Small',
                        medium: 'Medium',
                        large: 'Large',
                    },
                    icon: 'Icon',
                    icons: {
                        none: 'None',
                        campaign: 'Campaign',
                        target: 'Target',
                        visibility: 'Eye',
                        trendingUp: 'Trending up',
                        trendingDown: 'Trending down',
                        speed: 'Gauge',
                        play: 'Play',
                        screen: 'Screen',
                        people: 'People',
                        money: 'Money',
                        calendar: 'Calendar',
                        check: 'Tick',
                        warning: 'Warning',
                    },
                    signColors: 'Green above, red below',
                    compareWith: 'Compare with',
                    compare: {
                        none: 'Nothing',
                        previousPeriod: 'Previous period',
                        lastYear: 'Same period last year',
                        shift: 'Same period earlier by…',
                        dates: 'Set dates',
                    },
                    compareHelp: {
                        previousPeriod:
                            'The stretch just before, as long as this one: last 7 days against the 7 before, this week against last week, 1–14 March against 15–28 February. Every date filter on the widget and the dashboard filters it uses moves back.',
                        lastYear:
                            'The same dates a year earlier. Every date filter on the widget and the dashboard filters it uses moves back a year.',
                        shift: 'The same dates, moved back by as much as you choose.',
                        dates: 'These dates instead of the ones the filters set: on the date field chosen, or on every field with a date filter.',
                    },
                    amount: 'How many',
                    unit: 'Of',
                    from: 'From',
                    to: 'To',
                    dateField: 'Date field',
                    everyDateField: 'Every dated filter',
                    units: {
                        days: 'days',
                        daysOne: 'day',
                        weeks: 'weeks',
                        weeksOne: 'week',
                        months: 'months',
                        monthsOne: 'month',
                        years: 'years',
                        yearsOne: 'year',
                    },
                    help: 'Shows the first metric as one figure, with any other metrics listed under it.',
                    ringHelp:
                        'Shows the first metric, a percentage calculated field, as a ring filling to 100%, with any other metrics beside it.',
                    vsYear: 'vs {{year}}',
                    vsLastYear: 'vs last year',
                    vsPrevious: 'vs previous period',
                    vsEarlier: 'vs {{amount}} {{unit}} earlier',
                    vsDates: 'vs {{from}} – {{to}}',
                    aboveTarget: 'Above target',
                    belowTarget: 'Below target',
                },
                rolling: {
                    operator: 'in the last…',
                    period: 'Period',
                    periods: {
                        today: 'Today',
                        yesterday: 'Yesterday',
                        last_7_days: 'Last 7 days',
                        last_14_days: 'Last 14 days',
                        last_30_days: 'Last 30 days',
                        last_90_days: 'Last 90 days',
                        this_week: 'This week',
                        last_week: 'Last week',
                        this_month: 'This month',
                        last_month: 'Last month',
                        this_quarter: 'This quarter',
                        last_quarter: 'Last quarter',
                        year_to_date: 'Year to date',
                        last_12_months: 'Last 12 months',
                        last_year: 'Last year',
                    },
                },
                calc: {
                    title: 'Calculated fields',
                    manage: 'Calculated fields',
                    intro: 'Formulas over a source’s columns, worked out from each widget’s results. A measure is a number (a ratio, a difference, a count of rows) that widgets can plot or list as a metric; a dimension is a label (Over / Under, a bucket) they can group or filter by. Each source has its own, as sources don’t share column names.',
                    empty: 'No calculated fields for this source yet.',
                    newName: 'Calculation {{count}}',
                    name: 'Name',
                    kind: 'Type',
                    kinds: {
                        measure: 'Measure',
                        dimension: 'Dimension',
                    },
                    format: 'Format',
                    formats: {
                        number: 'Number',
                        percent: 'Percentage',
                    },
                    formula: 'Formula',
                    insertField: 'Insert a column',
                    grain: 'Work it out per',
                    grainHelp:
                        'The columns each value is worked out for before the widget groups the rows, e.g. Campaign to label each campaign. Leave empty to use the widget’s own dimensions.',
                    delete: 'Delete calculated field',
                    addMeasure: 'Add measure',
                    addDimension: 'Add dimension',
                    reference:
                        'Aggregate a column with sum(), avg(), min(), max() or count(); rows() counts the rows behind a value. Refer to another calculated field by its name in square brackets.',
                    help: {
                        measure:
                            'Aggregates only — e.g. sum(total_plays) / sum(expected_plays). Recomputed after grouping, so ratios stay correct.',
                        dimension:
                            "Returns a label for each row, e.g. if([Delivery] >= 1, 'Over', 'Under').",
                    },
                    examples: {
                        ratio: 'one figure as a share of another (format it as a percentage)',
                        rows: 'how many rows — campaigns, say — make up each value',
                        bucket: 'label each row by another calculated field',
                    },
                    errors: {
                        empty: 'Write a formula.',
                        invalid: 'This formula can’t be worked out.',
                        unexpected: 'Unexpected “{{text}}”.',
                        unexpectedEnd: 'The formula ends too early.',
                        expected: 'Expected “{{text}}”.',
                        unclosedString: 'A quote at position {{position}} is never closed.',
                        unclosedRef: 'A [ at position {{position}} is never closed.',
                        arity: '{{fn}}() takes {{count}} argument(s).',
                        aggregateNeedsField: '{{fn}}() takes a single column name.',
                        unknownFunction: 'There is no function called {{fn}}().',
                        unknownField: 'This source has no column called {{field}}.',
                        unknownRef: 'There is no calculated field called [{{name}}].',
                        cycle: '[{{name}}] refers back to itself.',
                        measureNeedsAggregate:
                            'A measure can’t use {{field}} on its own: wrap it in sum(), avg(), min(), max() or count().',
                        measureUsesDimension:
                            'A measure can’t use the dimension [{{name}}]; only another measure.',
                    },
                },
            },
        },
        Login: {
            metaTitle: 'Login',
            welcomeMessage:
                'Welcome to our new and improved UniLED platform! Please use your existing login credentials. We hope you enjoy the enhanced experience!',
            genericLoginError: 'Login Failed',
            emailInputLabel: 'Email Address',
            passwordInputLabel: 'Password',
            forgotPasswordLinkLabel: 'Forgot password?',
            signInButtonLabel: 'Log in',
            passwordResetSuccess: 'Your password has been updated successfully.',
            chooseUserType: 'Choose a user type to login as',
            userTypes: {
                [UserType.Admin]: 'Admin',
                [UserType.Agency]: 'Creative Agency',
                [UserType.Buyer]: 'Team',
                [UserType.Client]: 'Brand',
                [UserType.Owner]: '$t(CommonWords.Owner)',
            },
        },
        revalidate: {
            metaTitle: 'Revalidating your account...',
            successMessage: 'Your account has been revalidated. You will be redirected shortly.',
        },
        MyProfile: {
            title: 'My Profile',
            changePassword: 'Change Password',
            changePasswordDescription:
                'You will be logged out once your password has been changed.',
        },
        ForgotPasswordRequest: {
            emailInputLabel: 'Email',
            emailInputPlaceholder: "Enter your account's email address",
            submitLabel: 'Send me a reset link',
            invalidEmail: 'Please enter a valid email address.',
            metaTitle: 'Request Password Reset',
            title: 'Password Reset',
            subTitle: 'You will receive a password reset link by submitting your email below.',
        },
        ForgotPasswordCode: {
            tokenInputLabel: 'Reset Code',
            tokenInputPlaceholder: 'Enter the reset code',
            invalidEmail: 'Please enter a valid email address.',
            emailInputLabel: 'Email',
            emailInputPlaceholder: "Enter your account's email address",
            passwordInputLabel: 'Password',
            passwordInputPlaceholder: 'Enter your new password',
            passwordConfirmationInputLabel: 'Confirm Password',
            passwordConfirmationInputPlaceholder: 'Confirm your new password',
            submitLabel: 'Set my new password',
            resetEmailSuccess: 'An email containing the reset link has been sent to your inbox.',
            metaTitle: 'Reset Password',
            title: 'Password Reset',
        },
        CompleteRegistration: {
            metaTitle: 'Complete Registration',
            title: 'Complete Registration',
            submitLabel: 'Complete Registration',
        },
        AcceptInvite: {
            inviteLandingMetaTitle: 'Campaign Invite',
            inviteDescription: "You've been invited to the campaign {{name}}",

            inviteNoAccount: "Don't have an account?",
            inviteLogin: 'I already have an account',
            inviteRegister: 'Register here',

            emailInputLabel: 'Email',
            metaTitle: 'Complete Registration',
            registerFirstName: 'First Name',
            registerLastName: 'Last Name',
            passwordInputLabel: 'Password',
            passwordConfirmInputLabel: 'Confirm Password',
            registerSubmit: 'Register',

            registerSuccess:
                'An account has been successfully created.\n\nPlease verify your email address by following the instructions in the email we have sent you.',
        },
        VerifyEmail: {
            metaTitle: 'Verify Email',
            loginLabel: 'Go to Login',
            verifySuccess:
                'Your email has been successfully verified. You can now log in by following the link below.',
        },
    },
    Modules: {
        Admin: {
            Home: {
                metaTitle: 'Admin Panel',
                description: 'A collection of configuration screens to control the system.',
            },
            NotificationsList: {
                metaTitle: 'Notifications',
                description: 'View and manage notification report schedules.',
                empty: 'No notification reports yet.',
                table: {
                    reportId: 'Report ID',
                    reportName: 'Report Name',
                    status: 'Status',
                    active: 'Active',
                    paused: 'Paused',
                    frequency: 'Frequency',
                    lastRun: 'Last Run',
                    recipients: 'Recipients',
                    nextRun: 'Next Run',
                },
                create: {
                    newNotificationButton: 'New Notification',
                    dialogTitle: 'Create Notification',
                    submitButton: 'Create',
                    nameLabel: 'Report Name',
                    intervalLabel: 'Frequency',
                    intervalHelper: 'Example: daily, weekly, or cron string',
                    emailSubjectLabel: 'Email Subject',
                    emailBodyLabel: 'Email Body',
                    recipientsLabel: 'Recipients',
                    recipientsCount: '{{count}} email(s)',
                },
            },
            NotificationDetail: {
                metaTitle: 'Notification',
                description: 'View the notification report details.',
                reportIdLabel: 'Report ID',
                reportNameLabel: 'Report Name',
                intervalLabel: 'Frequency',
                emailSubjectLabel: 'Email Subject',
                emailBodyLabel: 'Email Body',
                recipientsLabel: 'Recipients',
                queriesLabel: 'Queries',
                detailsTitle: 'Notification Details',
                updateFrequencyLabel: 'Update Frequency',
                updateNotificationLabel: 'Update Notification',
                updateRecipientsLabel: 'Update Recipients',
                queriesTitle: 'Queries',
                lastRunAtLabel: 'Last Run At',
                nextRunAtLabel: 'Next Run At',
                scheduleStatusLabel: 'Schedule Status',
                activeStatusLabel: 'Active',
                pausedStatusLabel: 'Paused',
                pauseScheduleLabel: 'Pause Schedule',
                resumeScheduleLabel: 'Resume Schedule',
                createdByLabel: 'Created By',
                createdAtLabel: 'Created At',
                sendPreviewLabel: 'Send Preview Now',
                previewSendingLabel: 'Sending Preview...',
                previewQueuedFallbackLabel: 'Report preview queued.',
                dbQueryLabel: 'DB Query {{index}}',
                querySourceLabel: 'Source',
                cloneQueryLabel: 'Clone',
                deleteQueryLabel: 'Delete',
                saveQueryLabel: 'Save',
                expandQueryLabel: 'Expand',
                collapseQueryLabel: 'Collapse',
                expandAllLabel: 'Expand all',
                collapseAllLabel: 'Collapse all',
                addQueryLabel: 'Add Query',
                queryCountLabel: '{{saved}} saved, {{draft}} draft',
                queryDeletedLabel: 'Query deleted.',
                undoDeleteLabel: 'Undo',
                saveQueriesLabel: 'Save new queries',
                queryUnsavedLabel: 'Unsaved',
                queryFilenameLabel: 'Filename',
                sqlEditorLabel: 'SQL',
                deltaDeliveryTitle: 'Delta delivery',
                deltaEnabledLabel: 'Delta on',
                deltaDisabledLabel: 'Delta off',
                sendNewRowsLabel: 'Send new rows',
                sendUpdatedRowsLabel: 'Send updated rows',
                deltaKeyColumnsLabel: 'Delta key columns',
                deltaKeyColumnsPlaceholder: 'Click Detect columns to choose key columns.',
                deltaKeyColumnsManualHelper: 'Separate columns with commas or new lines.',
                detectColumnsLabel: 'Detect columns',
                detectingColumnsLabel: 'Detecting...',
                detectColumnsErrorFallback: 'Unable to detect columns.',
                deltaCacheLabel: 'Delta cache',
                deltaCacheLoadingLabel: 'Loading delta cache...',
                deltaCacheRowsCachedLabel: '{{count}} rows cached',
                deltaCacheEmptyLabel: 'No delta cache set',
                deltaCacheInactiveButStoredLabel: 'No active delta cache set (older cache exists).',
                deltaCacheUnreadableLabel: 'Delta cache is unreadable. Clear it to recover.',
                deltaCacheClearedLabel: 'Delta cache cleared.',
                deltaCacheLoadErrorFallback: 'Unable to load delta cache status.',
                deltaCacheClearErrorFallback: 'Unable to clear delta cache.',
                clearDeltaCacheLabel: 'Clear cache',
                clearDeltaCacheClearingLabel: 'Clearing...',
                clearDeltaCacheConfirmLabel: 'Confirm clear',
                seedDeltaBaselineLabel: 'Reset and seed baseline',
                seedDeltaBaselineConfirmLabel: 'Confirm baseline reset',
                seedDeltaBaselineQueueingLabel: 'Queueing baseline...',
                deltaBaselineQueuedLabel: 'Delta baseline seed queued.',
                deltaBaselineQueueErrorFallback: 'Unable to queue delta baseline seed.',
                jobStatusLabel: 'Job #{{id}}',
                runHistoryTitle: 'Run History',
                refreshRunHistoryLabel: 'Refresh history',
                reportRunsLoadingLabel: 'Loading history...',
                reportRunsLoadErrorFallback: 'Unable to load run history.',
                noReportRunsLabel: 'No report runs yet.',
                reportRunSummaryLabel: '{{type}} run - {{status}}',
                reportRunRowsLabel: '{{sent}} sent / {{current}} current rows',
                reportRunDeliveryLabel: 'Delivery: {{mode}}',
                reportRunFileLabel: 'File: {{file}} ({{size}})',
                reportRunRecipientsLabel: '{{count}} recipient(s)',
                expandRunLabel: 'Details',
                collapseRunLabel: 'Hide details',
                noReportQueryRunsLabel: 'No query run details.',
                queryRunSummaryLabel: '{{filename}} - {{status}}',
                queryRunDetailLabel:
                    '{{sent}} sent / {{current}} current rows - {{size}} - cache written: {{cache}}',
                bytesLabel: '{{count}} B',
                kilobytesLabel: '{{count}} KB',
                megabytesLabel: '{{count}} MB',
                yesLabel: 'yes',
                noLabel: 'no',
                loading: 'Loading report...',
                loadError: 'Unable to load report.',
                invalidId: 'Invalid report id.',
                noRecipients: 'No recipients.',
                noQueries: 'No queries.',
                emptyValue: '—',
            },
            SkinSwitcher: {
                defaultThemeOptionLabel: 'Default Skin',
                dropdownLabel: 'Choose Skin',
            },
            ColorCorrectionSwitcher: {
                label: 'Color Correction',
                none: 'None',
                protanope: 'Protanope',
                deuteranope: 'Deuteranope',
                tritanope: 'Tritanope',
            },
            DashboardSetup: {
                metaTitle: 'Dashboard Setup',
                editMetaTitle: 'Edit Dashboard',
                description:
                    'Manage saved dashboards and assign them to users, agencies, buyers, brands or media owners.',
                assignTitle: 'Who sees this dashboard',
                editorTitle: 'Edit this dashboard',
                assignButton: 'Save assignment',
                table: {
                    idCol: 'ID',
                    nameCol: 'Name',
                },
            },
            SummarySetup: {
                metaTitle: 'Summary Setup',
                editMetaTitle: 'Edit Summary',
                description:
                    'Manage saved campaign summaries and assign them to users, agencies, buyers, brands or $t(CommonWords.mediaOwners).',
                assignTitle: 'Who sees this summary',
                editorTitle: 'Edit this summary',
                table: {
                    idCol: 'ID',
                    nameCol: 'Name',
                    campaignTypeCol: 'Campaign type',
                },
            },
            SkinsList: {
                metaTitle: 'Manage Skins',
                description: 'Manage system skins in real time and assign them to users.',
                table: {
                    idCol: 'ID',
                    nameCol: 'Name',
                },
                create: {
                    nameLabel: 'New Skin Name',
                    newSkinButton: 'New Skin',
                    submitButton: 'Save New Skin',
                },
            },
            SkinEdit: {
                metaTitle: 'Edit Skin',
                description: "Update an individual skin's configuration.",
                pageTitle: 'Update {{name}}',
                form: {
                    overviewTitle: 'Overview',
                    largeLogoUploadLabel: 'Upload Large Logo',
                    smallLogoUploadLabel: 'Upload Small Logo',
                    largeDarkLogoUploadLabel: 'Upload Large Dark Mode Logo',
                    smallDarkLogoUploadLabel: 'Upload Small Dark Mode Logo',

                    assignTitle: 'Assign To Users',
                    assignSkin: 'Set Skin Assignment',

                    agencyAssignLabel: 'Creative Agencies',
                    buyerAssignLabel: 'Teams',
                    clientAssignLabel: 'Clients',
                    ownerAssignLabel: '$t(CommonWords.Owners)',
                    userAssignLabel: 'Users',

                    colorsTitle: 'Colours',
                    primaryColorLabel: 'Primary Colour',
                    secondaryColorLabel: 'Secondary Colour',
                    tertiaryColorLabel: 'Tertiary Colour',
                    quaternaryColorLabel: 'Quaternary Colour',
                    quinaryColorLabel: 'Quinary Colour',
                    senaryColorLabel: 'Senary Colour',
                    dividerColorLabel: 'Divider Colour',
                    cardColorLabel: 'Card Background Colour',
                    bgColorLabel: 'Background Colour',
                    navBgColorLabel: 'Navigation Background Colour',
                    primaryTextColorLabel: 'Primary Text Colour',
                    secondaryTextColorLabel: 'Secondary Text Colour',
                    disabledTextColorLabel: 'Disabled Text Colour',

                    darkModeTitle: 'Dark Mode',
                    darkModeAutoLabel: 'Automatically adjust colours for dark mode',
                    darkModeAutoDescription:
                        'Colours that would be hard to read on a dark background are lightened. Colours that are already readable are left as they are. This is how the colours will look:',
                    darkModeManualDescription:
                        'These colours are used in dark mode instead of the ones above. Any left empty fall back to the regular colour.',

                    formTabTitle: 'Forms',
                    typographyTabTitle: 'Typography',

                    save: 'Save Skin',
                },
            },
            RolesList: {
                metaTitle: 'Manage Roles',
                description: 'Manage system roles and control user access.',
                create: {
                    nameLabel: 'New Role Name',
                    newRoleButton: 'New Role',
                    submitButton: 'Save New Role',
                },
                table: {
                    idCol: 'ID',
                    nameCol: 'Name',
                },
            },
            RoleEdit: {
                metaTitle: 'Edit Role',
                description: 'Update a role.',
                permissionTab: 'Permissions',
                usersTab: 'Users',
                pageTitle: 'Update {{name}}',
                generalPermissionsHeader: 'General Permissions',
                adminPermissionsHeader: 'Admin Permissions',
                entityPermissionsHeader: 'Entity Permissions',
                saveLabel: 'Update Role Settings',
                selectAllPermissions: 'Select All',
                saveDialogTitle: 'Save Role',
                nameInputLabel: 'Name',
                agencyCheckboxLabel: 'Creative Agency Invite - Campaign Shareable link',
                reportCheckboxLabel: 'Reporting Invite - Campaign Shareable link',
                agencyCheckboxExplanation:
                    'When a user is invited to a campaign via the shareable link, this role will be automatically assigned to them when they register or accept the invitation.',
                permissionChangedTitle: 'Permission Changes',
                permissionsAdded: '{{count}} Added',
                permissionsRemoved: '{{count}} Removed',
                nameChangedWarning: 'Name changed from "{{from}}" to "{{to}}"',
                campaignAgencyInviteChangedWarning:
                    'Campaign Creative Agency Invite changed from "{{from}}" to "{{to}}"',
                campaignreportingChangedWarning:
                    'Campaign Reporting Invite changed from "{{from}}" to "{{to}}"',
            },
            UsersList: {
                metaTitle: 'Manage Users',
                description: 'Manage system users and control access.',
                createUser: 'New User',
            },
            UserCreate: {
                metaTitle: 'Create User',
                description: 'Add a new user to the system.',
            },
            UserForm: {
                firstName: 'First Name',
                lastName: 'Last Name',
                language: 'Language',
                email: 'Email Address',
                active: 'Active',
                campaigns: 'Campaigns',
                bulkAddCampaigns: 'Bulk Add Campaigns',
                bulkAddCampaignsPlaceholder: 'Paste campaign IDs, one per line',
                cancel: 'Cancel',
                apply: 'Apply',
                assignAssociations: 'Associations',
                associationsDescription:
                    'Assigning users to Teams, Agencies, Brands, $t(CommonWords.MediaOwners) and Roles will determine what information the user has access to and what actions they can perform on the system.',
                buyers: 'Teams',
                agencies: 'Creative Agencies',
                owners: '$t(CommonWords.MediaOwners)',
                brands: 'Brands',

                roles: 'Roles',
                roleDescription:
                    'A user can be assigned multiple roles. When multiple roles are assigned to a user, all of their roles will be combined into a single set of permissions.',

                oldPortalDisabled: 'Disable Old Portal Access',
                profilingEnabled: 'Profiling Enabled',

                failedToSave: 'Failed to save',
                saveSuccess: 'User Updated',
                saveBtn: 'Save',

                failedToCreate: 'Failed to create new user',
                createSuccess: 'User created',
                createBtn: 'Create New User',
            },
            UserEdit: {
                metaTitle: 'Edit User',
                description: 'Update user details.',
                pageTitle: 'Update {{name}}',
                impersonateUserHeader: 'Impersonate {{name}}',

                resendInvitationDescription:
                    'This user has not completed the registration verification process.',
                resendInvitationBtn: 'Resend Invite',

                detailsHeader: 'Details',
                legacyAssociationsHeader: 'Legacy Associations',
                legacyAssociationsDescription: 'Associations set in the old system.',
                legacyBuyerHeader: 'Team',
                legacyAgencyHeader: 'Creative Agency',
                legacyBrandHeader: 'Brand',
                legacyOwnerHeader: '$t(CommonWords.MediaOwner)',

                userDetailsTab: 'User Details',
                passwordTab: 'Update Password',

                passwordPageTitle: 'Update {{name}} Password',
                oldPassword: 'Current Password',
                newPassword: 'New Password',
                confirmNewPassword: 'Confirm New Password',
                savePassword: 'Save New Password',

                passwordSaveSuccess: 'Saved new password',
                passwordSaveFail: 'Failed to save new password',
            },
            AuthorizeMcp: {
                metaTitle: 'Authorize MCP Client',
                description:
                    'Approve an AI development tool to access UniLED data as you, for as long as you allow it.',
                prompt: 'An MCP client is asking to access UniLED as {{email}}.',
                scopeExplanation:
                    'It will be able to read whatever you can read, and only that. Access lasts 90 days unless your account is deactivated.',
                approve: 'Allow access',
                cancel: 'Cancel',
                incomplete:
                    'This page was opened without a valid authorization request. Start again from your MCP client.',
                failed: 'That authorization request is no longer valid. Start again from your MCP client.',
            },
            ImpersonateUser: {
                metaTitle: 'Impersonate User',
                noimpersonate: 'Unable to impersonate this user.',
                description:
                    'Provide system support by impersonating a user. You will be able to see the system as they do.',
                pageHelpDescription:
                    'Choose a user to impersonate below. When impersonating the user you will see the system the way they do.\n\nYou will have the same permissions, access to campaigns and other data. There will be a button at the top of the screen which will allow you to return to your user.',
                userDropdownLabel: 'Search for User',
                startImpersonateButton: 'Start Impersonating as {{type}}',
                genericFailureMessage: 'Failed to impersonate user',
                impersonateSuccess:
                    'Started impersonation successfully. Switching over to user now. This may take a few seconds.',
                stopImpersonateSuccess:
                    'Stopped impersonation. Switching back now. This may take a few seconds.',
                isAlreadyImpersonatingError:
                    'You are already impersonating. You must stop impersonating the current user before starting a new impersonation.',

                chooseUserType:
                    'This user has multiple types of associations. Choose a user type to impersonate as',
            },
            MarketList: {
                metaTitle: 'Markets',
                description: 'Manage markets',
                table: {
                    idCol: 'ID',
                    nameCol: 'Name',
                },
                create: {
                    newMarketButton: 'New Market',
                    submitButton: 'Create New Market',
                    nameLabel: 'Name',
                },
                update: {
                    updateTitle: 'Update "{{name}}"',
                    nameLabel: 'Name',
                },
            },
            Usage: {
                metaTitle: 'Platform usage',
                description: 'Who uses the platform, how much, and what for',
                subtitle:
                    'Who is using the platform, how much, and what for. Impersonated sessions are excluded.',
                featuresTitle: 'Feature usage',
                featuresDescription:
                    "Which parts of the portal people use, and which they don't. Use it to decide what to improve, what to make easier to find, and what to retire.",
                everyone: 'Everyone',
                organisationTitle: 'Organisation usage',
                userTitle: 'User activity',
                period: 'Period',
                includeStaff: 'Include staff',
                includeStaffHint:
                    'Staff are users with admin permissions. They are left out of the numbers unless included.',
                periodDays: 'Last {{count}} days',
                type: 'Type',
                allTypes: 'All organisations',
                search: 'Search',
                searchPlaceholder: 'Organisation name',
                loadFailed: 'Usage data could not be loaded.',
                noData: 'No activity recorded in this period.',
                vsPrevious: 'vs previous period',
                types: {
                    agency: 'Agency',
                    buyer: 'Buyer',
                    client: 'Client',
                    owner: '$t(CommonWords.MediaOwner)',
                    admin: 'Admin',
                },
                kpi: {
                    activeUsers: 'Active users',
                    sessions: 'Sessions',
                    timeInPortal: 'Time in portal',
                    campaignsViewed: 'Campaigns viewed',
                    activeDays: 'Active days',
                    activeOfTotal: '{{active}} of {{total}}',
                    typeAverage: 'type average {{share}}',
                    perActiveUser: '{{count}} per active user',
                    perSession: 'avg {{time}} per session',
                },
                activeUsersTitle: 'Active users',
                activeUsersHint: 'Everyone active in this period. Pick a column to sort by it.',
                noOrganisation: 'No organisation',
                metrics: {
                    active_users: 'Active users',
                    active_organisations: 'Organisations',
                    sessions: 'Sessions',
                    time_ms: 'Time in portal',
                    actions: 'Actions',
                    campaigns_created: 'Campaigns created',
                    live_campaigns: 'Live campaigns',
                },
                chartThis: 'Chart this',
                vsPreviousShort: 'vs previous',
                noPrevious: 'nothing to compare yet',
                liveOnLastDay: 'live on the last day',
                perDay: '{{metric}} per day',
                chartHint: 'Pick any number above to chart it instead.',
                weekday: 'Weekday',
                weekend: 'Weekend',
                previousPeriod: 'Previous period',
                organisationsHint:
                    'Every organisation, dormant ones included. Pick a column to sort, open one to see its people.',
                busiestOrganisations: 'Busiest organisations',
                upOnPrevious: 'Up on the previous period',
                downOnPrevious: 'Down on the previous period',
                goingQuiet: 'Going quiet',
                goingQuietHint: 'active last period, nobody this period',
                nobodyQuiet: 'Nobody has gone quiet.',
                lastSeenLabel: 'last seen',
                newThisPeriod: 'New this period',
                newSummary: '{{people}} people · {{organisations}} organisations',
                andMore: 'and {{count}} more',
                namesAndMore: '{{names}} and {{count}} more',
                nobodyNew: 'Nobody new this period.',
                whatPeopleDid: 'What people did',
                whatPeopleDidHint: 'saved, created, downloaded',
                byPeople: 'by {{count}}',
                emailsSent: 'Emails sent',
                emailsSentSummary_one: '{{sent}} to {{count}} person',
                emailsSentSummary_other: '{{sent}} to {{count}} people',
                noEmails: 'No emails sent in this period.',
                devicesMostly: '{{split}} · mostly {{usual}}',
                showAllPeople: 'Show all {{count}} people',
                organisations: 'Organisations',
                showingOf: 'Showing {{shown}} of {{total}}',
                previous: 'Previous',
                next: 'Next',
                columns: {
                    person: 'Person',
                    activePeople: 'Active people',
                    organisation: 'Organisation',
                    organisations: 'Organisations',
                    kind: 'Kind',
                    type: 'Type',
                    activeUsers: 'Active users',
                    sessions: 'Sessions',
                    time: 'Time in portal',
                    mostUsed: 'Most used',
                    lastActive: 'Last active',
                    trend: 'Trend',
                    user: 'User',
                    role: 'Role',
                    activeDays: 'Active days',
                    actions: 'Actions',
                    lastSeen: 'Last seen',
                    area: 'Area',
                    users: 'Users',
                    visits: 'Visits',
                    vsType: 'vs. others of this type',
                    page: 'Page',
                    people: 'People',
                    avgTime: 'Avg. time',
                    shareOfTime: 'Share of time',
                    feature: 'Feature',
                },
                never: 'Never',
                engagementTitle: 'User engagement',
                engagement: {
                    power: '{{count}} power users',
                    powerHint: 'active 15+ days',
                    regular: '{{count}} regular',
                    regularHint: 'active 5–14 days',
                    occasional: '{{count}} occasional',
                    occasionalHint: 'active 1–4 days',
                    dormant: '{{count}} dormant',
                    dormantHint: 'not seen in this period',
                },
                dormantChip: 'Dormant',
                whatTheyUse: 'What this organisation uses',
                comparison: {
                    above: 'Above',
                    typical: 'Typical',
                    below: 'Below',
                },
                users: 'Users',
                activityGrid: 'Activity, last 12 weeks',
                activityGridLabel: '{{date}}: {{count}} actions',
                campaignsMost: 'Campaigns looked at most',
                visitsCount: '{{count}} visits',
                recentActivity: 'Recent activity',
                loadMore: 'Show earlier activity',
                loadingMore: 'Loading earlier activity…',
                loadMoreFailed: 'Earlier activity could not be loaded.',
                tryAgain: 'Try again',
                mostUsed: 'Most used',
                actionsCount: 'Actions in this period',
                firstSeen: 'First seen {{date}}',
                lastSeen: 'Last seen {{date}}',
                kinds: {
                    page_view: 'Page',
                    event: 'Action',
                    request: 'Query',
                },
                pagesInApp: 'Pages in the portal',
                pagesInAppHint: 'every page people can open',
                pagesInAppHintNoAdmin: 'admin pages left out with staff',
                widelyUsed: 'In regular use',
                widelyUsedHint: '10+ people · {{share}}% of all time spent',
                rarelyUsed: 'Rarely used',
                rarelyUsedHint: 'only 1–3 people',
                neverUsed: 'Never used',
                neverUsedHint: 'nobody opened them in this period',
                mostUsedPages: 'Most used pages',
                mostUsedPagesHint: 'By share of all time spent in the portal',
                showAllPages: 'Show all {{count}} used pages',
                showFewer: 'Show fewer',
                neverUsedList: 'Never used',
                neverUsedListHint:
                    'No visits from anyone in this period. Worth retiring, hiding, or making easier to find.',
                pageCount_one: '{{count}} page',
                pageCount_other: '{{count}} pages',
                moreNames: '+{{count}} more',
                seeAllNever: 'See all {{count}}',
                nothingUnused: 'Every page was opened at least once.',
                adoptionByRole: 'Who uses what',
                adoptionHint: "Of each role's active people, the share who used it at least once.",
                featureKinds: {
                    page: 'Page',
                    event: 'Action',
                },
                few: 'Few',
                most: 'Most',
                noneActiveKey: '— nobody in that role was active',
                notApplicable: '—',
                pageVisit: 'Page visit',
                filtersUsed: 'Filters used',
                connection: 'From {{ip}} · {{device}}',
                devicesTitle: 'Devices',
                devicesHint: 'Share of sessions',
                deviceOn: '{{browser}} on {{os}}',
                deviceTypes: {
                    desktop: 'Desktop',
                    mobile: 'Mobile',
                    tablet: 'Tablet',
                    other: 'Other',
                },
                showDetails: 'Show details ({{count}})',
                hideDetails: 'Hide details',
                today: 'Today',
                yesterday: 'Yesterday',
                session: 'Session',
                activeDaysOfGrid: '{{active}} of {{total}} days',
                daySummary_one: '{{count}} session · {{time}}',
                daySummary_other: '{{count}} sessions · {{time}}',
                watchRecordings: 'Watch recordings in Clarity',
                watchRecordingsHint: 'Filter recordings by user_id {{id}}',
                usualBrowser: 'Usually {{device}}',
                kpiNotes: {
                    activeDaysOf: 'of the last {{count}}',
                    perActiveDay: '{{count}} per active day',
                    avgPerSession: 'avg {{time}} per session',
                    mostly: 'mostly #{{id}}',
                },
                emailSent: 'Email · {{name}}',
                emailSubject: 'Subject: {{subject}}',
                emailTo: 'To {{to}}',
                showEmail: 'Show email',
                hideEmail: 'Hide email',
                emailNoBody: 'Body not kept: it signs the user in',
                backgroundRequests: '{{count}} background requests',
                backgroundRequestsHint:
                    'API calls not made from a page view (before tracking started, or by another tool)',
                campaignRef: '#{{id}} · {{name}}',
                pagesTitle: 'Pages used most',
                less: 'Less',
                more: 'More',
                actionsTitle: 'Actions',
                events: {
                    export_cad: 'Exported CAD data',
                    generate_pca: 'Generated a PCA report',
                    export_chart_data: 'Downloaded chart data',
                    start_impersonation: 'Started impersonating a user',
                    stop_impersonation: 'Stopped impersonating',
                    created_campaign: 'Created a campaign',
                    uploaded_creatives: 'Uploaded creatives',
                    saved_schedule: 'Saved a schedule',
                    sent_pca_pdf: 'Sent a PCA PDF',
                    downloaded_pca_in_flight: 'Downloaded a PCA during the campaign',
                    downloaded_pca_post_campaign: 'Downloaded a PCA after the campaign',
                    downloaded_files: 'Downloaded files',
                    invited_someone: 'Invited someone',
                    created_report: 'Created a report',
                },
                eventsShort: {
                    export_cad: 'CAD',
                    generate_pca: 'PCA',
                    export_chart_data: 'Chart data',
                    start_impersonation: 'Impersonations',
                    stop_impersonation: 'Impersonation ends',
                    downloaded_pca_in_flight: 'PCA in flight',
                    downloaded_pca_post_campaign: 'PCA after',
                },
                timeOnPage: '{{time}} on page',
                queriesCount: '{{count}} queries',
                withoutPage: 'Not linked to a page',
                requests: {
                    campaignList: 'Campaign list',
                    campaignDetails: 'Campaign details',
                    campaignUpdate: 'Campaign saved',
                    campaignSchedules: 'Campaign schedules',
                    campaignScheduleScreens: 'Schedule screens',
                    brandDetails: 'Brand details',
                    brandList: 'Brand search',
                    buyerDetails: 'Buyer details',
                    buyerList: 'Buyer search',
                    dataStatus: 'Data status',
                    signedInUser: 'Signed-in user',
                    skin: 'Skin',
                    systemAlerts: 'System alerts',
                    activity: 'Activity',
                    campaignCounts: 'Campaign counts',
                    dynamicReport: 'Report builder',
                    livePerformance: 'Live performance',
                    pcaPdf: 'PCA PDF download',
                    pcaReport: 'Delivery report',
                    pcaSummary: 'Delivery summary',
                    pcaMap: 'Delivery map',
                    pcaStats: 'Delivery stats',
                    pcaHistogram: 'Delivery histogram',
                    pcaDsp: 'DSP delivery',
                    pcaBookedLines: 'Booked lines',
                },
                params: {
                    dates: 'Dates',
                    search: 'Search',
                    scheduled: 'Scheduled',
                    displayType: 'Display type',
                    groupBy: 'Group by',
                    view: 'View',
                    tab: 'Tab',
                    status: 'Status',
                    campaign: 'Campaign',
                    impactsPercentage: '$t(CommonWords.Impacts) %',
                    expectedPlays: 'Expected plays',
                    scope: 'Campaign status',
                    searchIn: 'Search {{what}}',
                },
                searchTargets: {
                    client: 'brands',
                    buyer: 'buyers',
                    agency: 'agencies',
                    campaign: 'campaigns',
                    creative: 'creatives',
                    user: 'users',
                },
                areas: {
                    reporting: 'Campaign reporting',
                    campaigns: 'Campaigns',
                    schedules: 'Schedules',
                    creatives: 'Creatives',
                    screens: '$t(CommonWords.Frame)s and screens',
                    organisations: 'Organisations',
                    dashboard: 'Dashboard',
                    account: 'Account and users',
                    admin: 'Admin',
                    other: 'Other',
                },
                duration: {
                    none: '—',
                    seconds: '{{s}}s',
                    minutes: '{{m}}m {{s}}s',
                    hours: '{{h}}h {{m}}m',
                },
            },
            FrameSearch: {
                metaTitle: '$t(CommonWords.Frame) Search',
                description: '$t(CommonWords.Frame) Search',
                frameCount: '{{count}} unique $t(CommonWords.frame)s',
                searchLabel: 'Search',
                checkboxLabel: 'Please tick if there are 300 or more $t(CommonWords.frame)s',

                frameExportReport: '$t(CommonWords.Frame) Export Report',
                conflictedFramesHeader: '$t(CommonWords.Frame)s for {{frame}}',
                uniqueResolutions: 'Unique Resolutions',
                copyFrameIds: 'Copy $t(CommonWords.Frame) Ids',

                missingFrames: 'Could not find {{count}} $t(CommonWords.frame)s: {{frames}}',
                conflictedFrames: 'The $t(CommonWords.frame) {{frame}} has a conflict',
                active: 'Active',
                inactive: 'Inactive',
                na: 'Not Found',
                yes: 'Yes',
                no: 'No',
                table: {
                    uploadedID: 'Uploaded ID',
                    frameIdCol: '$t(CommonWords.Frame) ID Added',
                    active: 'Active',
                    name: 'ScreenName',
                    idSource: 'Display ID Source',
                    id: 'Display ID Added',
                    lineItemName: 'Line Item Name',
                    displayNameCol: 'Display Name',
                    resolutionCol: 'Resolution',
                    mediaOwnerCol: '$t(CommonWords.MediaOwner)',
                    packNameCol: '$t(CommonWords.Pack) Name',
                    conflict: 'Conflict',
                    town: '$t(CommonWords.Town)',
                    tvRegion: '$t(CommonWords.TVRegion)',
                    conurbation: '$t(CommonWords.Conurbation)',
                },
            },
        },
        Main: {
            Dashboard: {
                campaigns: 'Campaigns',
                year: 'Year',
                allImpacts: 'All $t(CommonWords.Impacts)',
                totalImpacts: 'Total $t(CommonWords.Impacts)',
                targetImpacts: 'Target $t(CommonWords.Impacts)',
                additionalImpacts: 'Additional $t(CommonWords.Impacts)',
                impactDelivery: '$t(CommonWords.Impacts) Delivery',
                topFiveMediaOwners: 'Top 5 Performing $t(CommonWords.MediaOwners)',
                TopFivePerformingEnvrionments: 'Top 5 Performing Environments',
                percImpacts: '% $t(CommonWords.Impacts)',
                expectedImpacts: 'Expected $t(CommonWords.Impacts)',
                percImpactPerformance: '% $t(CommonWords.Impacts) Performance',
                impactPerformance: '$t(CommonWords.Impacts) Performance',
                playPerformance: 'Plays Performance',
                owner: '$t(CommonWords.Owner)',
                owners: '$t(CommonWords.Owners)',
            },
            Agency: {
                List: {
                    metaTitle: 'Creative Agencies',
                    listTitle: 'Search Creative Agencies',
                    description: 'Search and filter creative agencies',
                    newAgencyButton: 'New Creative Agency',
                    newAgencySubmit: 'Create new Creative Agency',
                    table: {
                        idCol: 'ID',
                        nameCol: 'Name',
                        campaignCount: 'Campaign Count',
                        marketCol: 'Market',
                    },
                    update: {
                        title: 'Update {{name}}',
                        nameLabel: 'Name',
                        marketLabel: 'Market',
                    },
                },
                Overview: {
                    Tabs: {
                        campaigns: 'Campaigns',
                        creative: 'Creatives',
                        creativeWarningsTooltip: 'Contains Creative Warnings',
                        users: 'Users',
                    },
                },
            },
            Buyer: {
                audienceSourceLabel: 'Audience Source',
                save: 'Save',
                List: {
                    metaTitle: 'Teams',
                    listTitle: 'Search Teams',
                    description: 'Search and filter teams',
                    newBuyerButton: 'New Team',
                    newBuyerSubmit: 'Create new Team',
                    table: {
                        idCol: 'ID',
                        nameCol: 'Name',
                        campaignCount: 'Campaign Count',
                        marketCol: 'Market',
                        userCol: 'Users',
                    },
                    create: {
                        nameLabel: 'Name',
                        marketLabel: 'Market',
                    },
                },
                Overview: {
                    Tabs: {
                        campaigns: 'Campaigns',
                        costs: 'Costs',
                        users: 'Users',
                    },
                    Costs: {
                        costCol: 'Cost Per Unit',
                        daysCol: 'Days Per Unit',
                        maxCostCol: 'Max Cost',
                        createdCol: 'Created At',

                        explanation:
                            'Per team cost configuration enables the system to work out the cost of a campaign depending on the number of line items, and the duration (in days) each line item will be used.',
                        newCostButton: 'Create New Cost',

                        newCostDialogTitle: 'Create New Team Cost',
                        newCostDays: 'Days Per Unit',
                        newCost: 'Cost Per Unit',
                        newMaxCost: 'Max Campaign Cost',
                    },
                    EditDialog: {
                        title: 'Edit Team',
                        nameLabel: 'Name',
                        marketLabel: 'Market',
                        refPrefixLabel: 'Campaign Ref Prefix',
                        refTypeLabel: 'Campaign Ref Types',
                        refMinLengthLabel: 'Campaign Ref Min Length',
                        refMaxLengthLabel: 'Campaign Ref Max Length',
                        refPreviewLabel: 'Campaign Ref Input Preview',
                        refTypes: {
                            [BuyerReferenceType.Any]: 'Any',
                            [BuyerReferenceType.Alpha]: 'Alpha',
                            [BuyerReferenceType.Alphanumeric]: 'Alphanumeric',
                            [BuyerReferenceType.Numeric]: 'Numeric',
                        },
                        surveySource: 'Survey Source',
                        linearVerifiedLabel: 'Linear Verified',
                        bookingSummaryValidatedLabel: 'Booking Summary Validated',
                    },
                },
            },
            Client: {
                List: {
                    metaTitle: 'Brands',
                    description: 'Brand List',
                    listTitle: 'Search Brands',
                    newClientButton: 'New Brand',
                    newClientSubmit: 'Create new Brand',
                    table: {
                        idCol: 'ID',
                        nameCol: 'Name',
                        marketCol: 'Market',
                    },
                    update: {
                        title: 'Update {{name}}',
                        nameLabel: 'Name',
                        marketLabel: 'Market',
                    },
                },
            },
            Owner: {
                List: {
                    metaTitle: '$t(CommonWords.Owners)',
                    listTitle: 'Search $t(CommonWords.Owners)',
                    description: 'Search and filter $t(CommonWords.owners)',
                    newOwnerButton: 'New $t(CommonWords.Owner)',
                    newOwnerSubmit: 'Create new $t(CommonWords.Owner)',
                    table: {
                        idCol: 'ID',
                        nameCol: 'Name',
                        marketCol: 'Market',
                    },
                    update: {
                        title: 'Update {{name}}',
                        nameLabel: 'Name',
                        marketLabel: 'Market',
                    },
                },
                Overview: {
                    Tabs: {
                        users: 'Users',
                    },
                },
            },
            Campaigns: {
                Invite: {
                    metaTitle: 'Campaign Invite',
                    inviteDescription:
                        "You've been invited to the campaign {{name}}, do you accept?",
                    declineLabel: 'Decline',
                    acceptLabel: 'Accept',
                    inviteNotAllowed:
                        'You are trying to access the creative agency upload section for campaign "{{campaign_name}}". You cannot accept this invitation as you have an association with {{association}}, please contact support@uniledsoftware.com if you feel you need to access this section of the campaign',
                },
                Ai: {
                    ButtonLabel: 'AI Assistant (Beta)',
                    title: 'Campaign - AI Assistant (Beta)',
                    messagePlaceholder: 'Type a message...',
                    accuracyMessage:
                        'This assistant can make mistakes, double check important information.',
                    aiNoMessages: 'send a message to start a chat',
                    restartChat: 'Restart Chat',
                },
                List: {
                    metaTitle: 'Campaigns',
                    listTitle: 'Search Campaigns',
                    description: 'Search and filter campaigns',
                    table: {
                        idCol: 'ID',
                        nameCol: 'Name',
                        referenceCol: 'Reference',
                        statusCol: 'Status',
                        brandCol: 'Brand',
                        buyerCol: 'Team',
                        displayCountCol: 'Displays',
                        dateRangeCol: 'Dates',
                        startDateCol: 'Start Date',
                        endDateCol: 'End Date',
                        reportingCol: '$t(CommonWords.Impacts)',
                        playoutOverallTooltip: 'Overall $t(CommonWords.Impacts) Delivery',
                        playoutNoExpectedImpactsTooltip:
                            '$t(CommonWords.Impacts) delivered, but none were expected',
                        playoutPlaysTooltip:
                            'No $t(CommonWords.Impacts) expected or delivered, so this is Plays Delivery',
                        playoutByMediaOwnerTooltip: 'By $t(CommonWords.MediaOwner):',
                    },
                    createCampaign: 'New Campaign',
                    scopes: {
                        upcoming: 'Upcoming',
                        live: 'Live',
                        past: 'Past',
                        archived: 'Archived',
                        hasErrors: 'Has Creative Errors',
                        awaitingData: 'Missing Data',
                        inspectingData: 'Data Query',
                        awaitingBookingPlan: 'Missing Booking Data',
                        inflightReportSent: 'Inflight Report Sent',
                        pcaReportSent: 'CAD Report Sent',
                        pcaReportNotSent: 'CAD Report Not Sent',
                        inflightReportNotSent: 'Inflight Report Not Sent',
                    },
                    statusDropdownLabel: 'Status',
                    startDateDropdownLabel: 'Start Date',
                    endDateDropdownLabel: 'End Date',
                    mediaOwnersDropdownLabel: '$t(CommonWords.MediaOwners)',
                    reportingPercentageLabel: 'Reporting Percentage',
                    bookingSummarySignOffLabel: 'Booking Summary',
                    signOffLabel: 'Sign Off',
                    signedOffOptionLabel: 'Signed Off',
                    notSignedOffOptionLabel: 'Not Signed Off',
                    highPriorityLabel: 'High Importance',
                    highPriorityOptionLabel: 'High Importance',
                    notHighPriorityOptionLabel: 'Not High Importance',
                },
                Create: {
                    currentStepLabel: 'Step {{current}}/{{total}}',
                    stepDetailsTitle: 'New Campaign Details',
                    stepLineItemsTitle: 'New Campaign Displays',
                    stepScheduleTitle: 'New Campaign Scheduling',
                    stepSummaryTitle: 'New Campaign Summary',
                    campaignType: 'Campaign Type',

                    metaTitle: 'Create New Campaign',
                    description: 'Create a new campaign',

                    detailsStepLabel: 'Details',
                    chooseDisplaysStepLabel: 'Choose Displays',
                    scheduleStepLabel: 'Scheduling',
                    summaryStepLabel: 'Summary',

                    nextStepLabel: 'Next',
                    prevStepLabel: 'Go Back',
                    createCampaignLabel: 'Create Campaign',

                    LineItemsTable: {
                        nameHeader: 'Name',
                        ownerHeader: '$t(CommonWords.MediaOwner)',
                        typeHeader: 'Type',
                        environmentHeader: 'Environment',
                        sizeHeader: 'Format',
                        resolutionHeader: 'Resolution (W x H)',
                        popshotsHeader: 'Images Uploaded',
                        assignedCreativesHeader: 'Assigned Creatives',
                        creativesAvailableHeader: 'Creatives Available',
                        fileTypesHeader: 'File Types',
                        aspectRatioHeader: 'Aspect Ratio',
                        resultsSelected: '{{count}} Selected',
                        lineItemSelected: 'Selected',
                        addLineItem: 'Add',
                        addedLineItem: 'Added',
                        startDateHeader: 'Start Date',
                        endDateHeader: 'End Date',
                        actionsHeader: 'Actions',
                        daysHeader: 'Total Live Days',
                        costHeader: 'Cost',

                        popShotsNoImages: '[no image has been uploaded]',
                        groupFilterLabel: 'Group',

                        assignCreatives: {
                            autoAssign: 'Auto Assign',
                            autoAssignFailed: 'Auto Assign Failed',
                            massUnAssign: 'Auto UnAssign',
                            massUnAssignFailed: 'Auto UnAssign Failed',

                            manualAssign: 'Assign Creatives',

                            dialogDescription:
                                'Please choose which creatives to assign to this display to by checking the checkboxes below',
                            assignSubmitBtn: 'assign selected',

                            failedToLoadAssignData: 'Failed to load',
                            failedToLoadAssignDataRetry: 'Reload',

                            failedToAssign:
                                'An error has occurred while trying to assign creatives to the display',

                            unAssignDialogDescription:
                                'Please choose which creatives to unassign from this display to by checking the checkboxes below',
                            unAssignSubmitBtn: 'unassign selected',

                            searchLabel: 'Search',
                            durationLabel: 'Duration',
                            resolutionDropdownLabel: 'Resolution (WxH)',
                            groupDropdownLabel: 'Group',
                            typeDropdownLabel: 'Type',
                            ratioDropdownLabel: 'Ratio',
                        },
                    },

                    bulkAttachMissingMarket:
                        'The Team you have selected is not assigned to a market.',
                    bulkAttachTitle: 'Bulk Attach $t(CommonWords.Frame)s',
                    bulkAttachScreenInfo: 'This is for digital screens only',
                    bulkAttachDescription:
                        'Paste a list of $t(CommonWords.frame) IDs (separated by new lines) below and we will automatically add them to the campaign.',
                    bulkAttachFrameCount: '{{count}} unique $t(CommonWords.frame)s',
                    bulkAttachFramesSubmit: 'Add $t(CommonWords.frame)s',
                    bulkAttachFramesSelect: 'Add Displays',
                    bulkAttachFramesInvalidError: 'Failed to find {{count}} $t(CommonWords.frame)s',
                    bulkAttachFramesError: 'Failed to find displays',
                    bulkAttachButtonLabel: '$t(CommonWords.Frame) List',

                    bulkAttachConflictDescription:
                        'Displays containing the same $t(CommonWords.Frame) ID have been found.\n\nPlease choose which display to use for each $t(CommonWords.Frame) ID.',
                    bulkAttachConflictFrameLabel: '$t(CommonWords.Frame)',

                    LineItemsSummary: {
                        allOwnersHeader: 'All Line Items',
                        noOwnersHeader: 'No $t(CommonWords.MediaOwner)',
                    },

                    campaignNameLabel: 'Campaign Name',
                    referenceLabel: 'Reference',
                    buyerDropdownLabel: 'Team',
                    clientDropdownLabel: 'Brand',
                    additionalContactsDropdownLabel: 'Campaign Contacts',
                    agencyAssignedDropdownLabel: 'Agency',

                    Costing: {
                        daysInputPlaceholder: '{{count}} days',
                        daysInputError: 'From 1 to {{max}}',
                        total: 'Total:',
                        lineItemCount: '{{count}} line items',
                        applyAllDays: 'Apply',
                        applyAllPlaceholder: 'Days',
                    },

                    Summary: {
                        campaignName: 'Campaign Name',
                        campaignRef: 'Ref',
                        campaignDates: 'Dates',
                        campaignDatesValue: '{{start}} to {{end}} ({{days}} days)',
                        campaignAgency: 'Agency',
                        campaignClient: 'Brand',
                        campaignBuyer: 'Team',
                    },

                    Blockers: {
                        nextIntro: 'To continue:',
                        createIntro: 'To create the campaign:',
                        name: 'Enter a campaign name of at least 3 characters',
                        reference: "Enter a reference in the format the team's settings ask for",
                        dates: 'Pick the start and end dates',
                        buyer: 'Choose a team',
                        client: 'Enter a brand',
                        lineItems: 'Add at least one display',
                        lineItemDays: 'Give every display from 1 to {{max}} live days',
                        other: 'Correct the fields marked in red',
                    },

                    Draft: {
                        restored:
                            'We restored the campaign you were setting up in this tab, where you left off.',
                        startOver: 'Start over',
                    },

                    creating: 'Creating Campaign',
                    failedToCreate: 'Failed to create campaign.',
                },
                CloneCampaign: {
                    metaTitle: 'Clone Campaign',
                    cloneBreadcrumb: 'Clone',
                },
                EditLineItems: {
                    metaTitle: 'Update Campaign Displays',
                    editLineItemsBreadcrumb: 'Update Displays',
                    editLink: 'Update Campaign',
                    cloneCampaign: 'Clone Campaign',

                    detailsStepLabel: 'Dates',
                    chooseDisplaysStepLabel: 'Update Displays',
                    scheduleStepLabel: 'Scheduling',
                    summaryStepLabel: 'Summary',

                    stepDetailsTitle: 'Update Dates',
                    stepChooseDisplaysTitle: 'Choose Displays',
                    stepScheduleTitle: 'Scheduling',
                    stepSummaryTitle: 'Summary',

                    prevStepLabel: 'Back',
                    nextStepLabel: 'Next',
                    saveCampaignLabel: 'Save',

                    lineItemsAdded: '{{count}} Displays Added',
                    lineItemsRemoved: '{{count}} Displays Removed',

                    saving: 'Saving Campaign',
                    failedToSave: 'Failed To Save',
                },
                Overview: {
                    metaTitle: 'Campaign Summary',
                    pageTitle: 'Campaign Summary',
                    description: 'View and update campaign specific information.',
                    noTabAccess: "You don't have access to any section of this campaign.",
                    backToList: 'Back to campaigns',
                    popImagesComingSoon: 'Pop images are on their way',
                    pcaDataComingSoon: "Your data is on it's way",
                    pcaDataComingSoonBtnLabel: 'Back to campaign overview',
                    underConstructionWarning:
                        'The screens are being added to your campaign and will be ready soon',
                    campaignArchivedPageStatus: 'This campaign has been archived',
                    missingDisplay: {
                        title: 'Missing Displays',
                        mediaNameTitle: 'Name',
                        frameIdTitle: '$t(CommonWords.Frame) ID',
                        mediaOwnerTitle: '$t(CommonWords.MediaOwner)',
                        screenNameSummaryTitle: 'Screen Name Summary',
                        environmentTitle: 'Environment',
                        formatTitle: 'Format',
                        lineSpaceItemTitle: 'Line Space Item',
                    },
                    OverviewSection: {
                        bookedByHeader: 'Booked by',
                        campaignType: 'Campaign Type',
                        brandHeader: 'Brand',
                        expectedImpacts: 'Expected $t(CommonWords.Impacts)',
                        expectedImpactsDerived:
                            'Sum of the insertion order targets on the schedule page',
                        boughtValue: 'Bought Value',
                        deliveredValue: 'Delivered Value',
                        buyerHeader: 'Team',
                        duration: 'Duration',
                        startDate: 'Start Date',
                        endDate: 'End Date',
                        campaignName: 'Campaign Name',
                        campaignId: 'Campaign ID',
                        reference: 'Reference',
                        agencyHeader: 'Media Agency',
                        isDsP: 'DSP',
                        isVistar: 'Vistar',
                        client: 'Client',
                        internal: 'Internal',
                        unlinked: 'Unlinked CF Report',
                        controls: 'Controls',
                        settings: 'Settings',
                        manageCampaign: 'Manage',
                        actions: 'Actions',
                    },
                    EditDialog: {
                        title: 'Edit Campaign',
                        campaignNameLabel: 'Campaign Name',
                        referenceLabel: 'Reference',
                        agencyDropdownLabel: '{{name}} ({{campaignsCount}} campaigns)',
                    },
                    ShareDialog: {
                        shareCopied: 'Copied!',
                        sharedWithUsers: 'Shared With ({{count}})',
                        generateAgencyLink: 'Generate Agency Link',
                        generateReportingLink: 'Generate Reporting Link',
                        table: {
                            user: 'User',
                            via: 'Via',
                            associationDate: 'Association Date',
                        },
                    },
                    ActionLabels: {
                        edit: 'Edit',
                        campaignOverview: 'Campaign Overview',
                        campaignDetails: 'Dates & Line Items',
                        share: 'Share',
                        archive: 'Archive',
                        unarchive: 'Unarchive',
                        clone: 'Clone',
                        downloadSpec: 'Spec',
                        uploadArtwork: 'Upload Artwork',
                        downloadCreatives: 'Creatives',
                        validateCreatives: 'Validate Creatives',
                        bookingSummary: 'Booking Summaries',
                        campaignSchedule: 'Campaign Schedule',
                        cad: 'CAD',
                        pca: 'PCA',
                        ioIds: `IO ID's`,
                        apply: 'Apply',
                        happyDemics: 'Brand Uplift Study',
                        reportingInputs: 'Reporting Inputs',
                        customizeSummary: 'Customise Summary',
                        customizePca: 'Customise PCA',
                    },
                    ArchiveDialog: {
                        title: 'Archive this campaign?',
                        description:
                            'It will be marked as archived. You can unarchive it at any time.',
                    },
                    SummaryCustomization: {
                        description:
                            'Choose which summary widgets to show and how they are grouped.',
                        widgetLabel: 'Widget {{value}}',
                        metric: 'Metric',
                        chartType: 'Chart Type',
                        chartTypes: {
                            line: 'Line',
                            bar: 'Bar',
                            map: 'Map',
                        },
                        mapView: 'View By',
                        mapDisplay: 'Show',
                        primaryGroupBy: 'Primary Group By',
                        secondaryGroupBy: 'Secondary Group By',
                        dragToReorder: 'Drag to reorder',
                        heatmapOnly: 'Heatmap',
                        heatmapAndScreens: 'Heatmap + Screens',
                        addWidget: 'Add Widget',
                        reset: 'Reset',
                        none: 'None',
                    },
                    PcaCustomization: {
                        // Labels for the option dropdown values (group-by fields, datasets,
                        // metrics, chart kinds, summary rows). Rendered via t() in the dialog.
                        optionLabels: {
                            display_owner: '$t(CommonWords.MediaOwner)',
                            display_env: 'Environment',
                            display_format: 'Format',
                            display_env_format: 'Environment & Format',
                            display_tv_region: '$t(CommonWords.TVRegion)',
                            display_conurbation: '$t(CommonWords.Conurbation)',
                            day_of_week: 'Day of Week',
                            hour_of_day: 'Hour of Day',
                            running_totals_impacts: 'Running Total $t(CommonWords.Impacts)',
                            running_totals_plays: 'Running Total Plays',
                            impacts: '$t(CommonWords.Impacts)',
                            plays: 'Plays',
                            time: 'Time',
                            bar: 'Bar',
                            line: 'Line',
                            performance: 'Performance',
                            total: 'Total',
                            expected: 'Expected',
                            variance: 'Variance',
                            actual: 'Actual',
                            maintainGrow: 'Maintain / Grow',
                            reviewConsider: 'Review / Consider',
                        },
                        description:
                            'Build and review the custom PCA report config from the sidebar. Advanced structures still flow through the generated JSON preview.',
                        advancedHint:
                            'This first version edits the main slide options directly and gives you a live JSON preview for the rest.',
                        startFrom: 'Start From Base',
                        resetToBase: 'Reset To Base',
                        reportSettings: 'Report Settings',
                        reportName: 'Report Name',
                        previewSkinId: 'Preview Skin ID',
                        previewSkinIdHint: 'Use a real skin ID so the slide preview can load.',
                        baseTemplate: 'Base Template',
                        variant: 'Variant',
                        slides: 'Slides',
                        addSlide: 'New Slide Type',
                        add: 'Add',
                        remove: 'Remove',
                        slideSettings: 'Slide Settings',
                        jsonPreview: 'JSON Preview',
                        copyJson: 'Copy JSON',
                        copied: 'Copied',
                        previewSlide: 'Preview Slide',
                        previewFullReport: 'Preview Full Report',
                        fullPreview: 'Full Report Preview',
                        printPreview: 'Print Preview',
                        choosePreviewSkin: 'Choose Preview Skin',
                        choosePreviewSkinHint:
                            'Select the skin to use for this slide preview before continuing.',
                        continuePreview: 'Continue Preview',
                        enabled: 'Enabled',
                        includeSlide: 'Include Slide',
                        slideId: 'Slide ID',
                        layout: 'Layout',
                        preset: 'Preset',
                        title: 'Title',
                        subtitle: 'Subtitle',
                        mainTitle: 'Main Title',
                        heading: 'Heading',
                        headlineCount: 'Summary Card Count',
                        summaryCards: 'Summary Cards',
                        summaryCardRows: 'Summary Card Rows',
                        showPerformanceHeadlines: 'Show Performance Headlines',
                        showCallToAction: 'Show UniLED Link Box',
                        splitWidth: 'Split Width',
                        footerColour: 'Footer Colour',
                        slideColour: 'Slide Colour',
                        primary: 'Primary',
                        thirdColour: 'Third Colour',
                        chart: 'Chart',
                        leftChart: 'Left Chart',
                        rightChart: 'Right Chart',
                        chartTitle: 'Chart Title',
                        dataset: 'Dataset',
                        dataSource: 'Data Source',
                        groupByFields: 'Group By Fields',
                        groupByField: 'Group By Field',
                        addGroupBy: 'Add Group By',
                        removeGroupBy: 'Remove Group By',
                        metric: 'Metric',
                        chartKind: 'Chart Kind',
                        maxItems: 'Max Items',
                        fontSize: 'Font Size',
                        showTriplet: 'Show Triplet',
                        showValueLabels: 'Show Value Labels',
                        showExpected: 'Show Expected',
                        includeZeroValues: 'Include Zero Values',
                        chartHeight: 'Chart Height',
                        leftPillText: 'Left Pill Text',
                        rightPillText: 'Right Pill Text',
                        mapTitle: 'Map Title',
                        mapMode: 'Map Mode',
                        leftMap: 'Left Map',
                        rightMap: 'Right Map',
                        heat: 'Heat',
                        markers: 'Markers',
                        columns: 'Columns',
                        sortBy: 'Sort By',
                    },
                    CustomReport: {
                        deliveryData: 'Delivery Data',
                        insights: 'Insights',
                        campaignSummary: 'Campaign Summary',
                        campaignLiveDates: 'Campaign Live Dates:',
                        campaignLiveDatesNoColon: 'Campaign Live Dates',
                        reportDataRange: 'Report Data Range:',
                        currentReportDates: 'Current Report Dates',
                        campaignNumber: 'Campaign Number:',
                        campaignNumberNoColon: 'Campaign Number',
                        performanceHeadlines: 'Performance Headlines',
                        performance: 'Performance',
                        total: '$t(Modules.Main.Campaigns.Overview.Analytics.actual)',
                        expected: '$t(Modules.Main.Campaigns.Overview.Analytics.expected)',
                        variance: '$t(Modules.Main.Campaigns.Overview.Analytics.variance)',
                        viewFullCampaignPerformance:
                            'Click here to view the full campaign performance on UniLED',
                        thankYou: 'Thank You',
                        glossary: 'Glossary',
                        glossaryOf: 'Glossary of',
                        terms: 'Terms',
                    },
                    HappyDemics: {
                        title: 'Brand Uplift Study',
                        submit: 'Submit',
                        select: 'Select Happy Demics Option',
                        search: 'Search by Brand Uplift Study Name or ID',
                        selected: 'Selected',
                        remove: 'Remove',
                        awaitingData: 'Data coming soon',
                        happydemicsReport: 'Happy Demics Report',
                        enabled: 'You need to have Brand Uplift Study enabled to use this feature',
                        happydemicsCampaign: 'Brand Uplift Study Campaign',
                        happyDemicsId: 'Happy Demics ID',
                        selectCol: 'Select',
                    },
                    Pca: {
                        title: 'PCA',
                        submit: 'Submit PPT',
                        submitPdf: 'Submit PDF',
                        pdfGenerating: 'Generating PDF',
                        yes: 'Yes',
                        no: 'No',
                        pdfReadyTitle: 'PDF ready',
                        pdfReadyMessage: 'Your PDF is ready. Download now?',
                        pdfFailed: "We couldn't generate the PDF. Please try again.",
                        linearReport: 'Linear report',
                        dspReport: 'DSP report',
                        success: 'PCA power point will be emailed to you shortly',
                        submitCurrentSkin: 'Submit With Current Skin',
                        submitDefaultSkin: 'Submit With Default Skin',
                        thirdColor: 'Third Color',
                    },
                    Cad: {
                        title: 'CAD',
                        cadType: 'Upload Or Download',
                        upload: 'Upload',
                        download: 'Download',
                        pleaseSelect: 'Please Select...',
                        Download: 'Download',
                        template: 'Generate template CSV',
                        environment: 'Environment',
                        workerRam: 'Worker RAM',
                        downloadSuccess: 'Report has been queued',
                        client: 'Client',
                        internal: 'Internal',
                        lastRunAt: 'Last Run At',
                        noInsertionOrders:
                            'No insertion orders are attached to this campaign. Add them on the schedule page.',
                        never: 'Never',
                        runSuccess: 'Pipeline task queued',
                        deleteSuccess: 'Pipeline task queued',
                        publishSuccess: 'Pipeline task queued',
                        run: 'Run',
                        delete: 'Delete',
                        publish: 'Publish',
                        FilteredExport: {
                            tooltip: 'Download CAD for the current filters',
                            title: 'Download filtered CAD',
                            description:
                                'Queues a raw CAD export containing only the rows that match the filters below.',
                            environment: 'Environment',
                            dateRange: 'Dates',
                            allDates: 'All dates (including outside the campaign dates)',
                            filters: 'Filters',
                            noFilters: 'No filters applied, so this will export the full dataset.',
                            partialWarning:
                                "This file is a subset of the campaign data. Do not re-import it: importing a CAD replaces all of the campaign's data for that environment.",
                            submit: 'Queue export',
                            success:
                                'Export queued. The download link will appear in the task queue when it is ready.',
                        },
                    },
                    Tabs: {
                        displays: 'Displays',
                        combined: 'Combined',
                        classicPostings: 'Classic Postings',
                        popImages: 'Pop Images',
                        viewToFill: 'View2Fill',
                        images: 'Pop Images',
                        creatives: 'Creatives',
                        reporting: 'Reporting',
                        cost: 'Costs',
                        analyticsSummaryReports: 'Summary',
                        playsReports: 'Plays',
                        impactsReports: '$t(CommonWords.Impacts)',
                        timeReports: 'Time Played',
                        dsp: 'DSP',
                        energyUsage: 'Energy Usage',
                        displayVerification: 'Display Verification',
                    },
                    dsp: {
                        route: 'Route Audited',
                        routeVistar: 'Non Route Audited',
                        geopath: 'Geopath Audited',
                        vistar: 'Non Geopath Audited',
                        audited: 'Route Audited Frames',
                        nonAudited: 'Non Route Audited Frames',
                        combinedKeyCol: '$t(CommonWords.MediaOwner) | Environment | Format',
                        mediaOwnerCol: '$t(CommonWords.MediaOwner)',
                        environmentCol: 'Environment',
                        formatCol: 'Format',
                        TTDPlaysCol: 'TTD Plays',
                        VIOOHPlaysCol: 'VIOOH Plays',
                        sumOfVioohImpressions: 'Sum of VIOOH Impressions',
                        sumOfTTDImpressions: 'Sum of TTD Impressions',
                        sumOfRouteDeliveredImpacts: 'Sum of Route Delivered Impacts',
                        count: 'Count',
                        value: 'Value',
                        top: 'Greater Value',
                        bottom: 'Less Value',
                        all: 'All',
                        uploadFile: 'Click  or drag and drop to upload a file',
                        displayOwner: 'Display Owner',
                        displayFormat: 'Display Format',
                        plays: '# Plays',
                        dspImpressions: 'DSP $t(CommonWords.Impacts)',
                        dspIm: 'Average DSP IM',
                        geopathIm: 'Average Geopath IM',
                        routeIm: 'Average Route IM',
                        geopathImpressions: 'Geopath $t(CommonWords.Impacts)',
                        routeImpressions: 'Route $t(CommonWords.Impacts)',
                        averageFormatIm: 'Average Format IM',
                        averageDSPIm: 'Average DSP IM',
                        var: '% VAR',
                        imVar: 'IM VAR',
                        var2: 'VAR',
                    },
                    Displays: {
                        displayCountHeader: 'All Displays ({{count}})',
                        noDisplays: 'No displays',
                    },
                    BookingSummary: {
                        title: 'Campaign Booking Summaries',
                        dropzoneText: 'Drag & Drop File or Click Here to Upload',
                        save: 'Save Notes',
                    },
                    Reports: {
                        overallTabTitle: 'Overall',
                        byFieldButton: 'Group by',
                        byMap: 'Map view',
                        viewBy: 'View by',
                        filterBy: 'Filter by',
                        dataStatusDropdown: 'Inspect',
                        errorTitle: 'No Data',
                        errorDescription: 'Some fatal error occured',
                        refresh: 'Refresh',
                        lineItemsUnderInvestigation: 'Line Items Under Investigation',
                        orderLineErrors: 'Order Line Errors',
                        orderLineErrorsTable: {
                            orderlineId: 'Order Line ID',
                            message: 'Message',
                            mediaOwner: '$t(CommonWords.MediaOwner)',
                            environment: 'Environment',
                            format: 'Format',
                            startDate: 'Start Date',
                            endDate: 'End Date',
                            quantity: 'Quantity',
                            updatedAt: 'Updated At',
                            tradingType: 'Trading Type',
                        },
                        stickers: {
                            today: 'Today',
                            week: 'Week',
                        },
                        frameID: '$t(CommonWords.Frame) ID',
                        campaignDates: 'Campaign Dates',
                        pageDates: 'Page Dates',
                        exportPca: {
                            exportPcaTitle: 'Export Reporting Data',
                            exportPcaDescription:
                                'An email will be sent containing links to the data files.',
                            exportPcaDescription2:
                                'Please allow up to 30 minutes for the email to arrive.',
                            exportPcaBtnLabel: 'Export',
                            exportPcaBtnLabelLoading: 'Loading',
                            exportPcaBtnLabelClose: 'Close',
                            exportPcaBtnLabelFailed: 'Try Again',

                            exportSuccess: 'Success',
                            exportFailed:
                                'Failed to export. Please try again or contact an administrator.',
                        },
                        [PCAReportMetric.Display]: {
                            expectedLabel: 'Expected Display Days',
                            actualLabel: 'Total Display Days',
                            percentageLabel: 'Display Days Percentage',
                            performanceLabel: 'Display Days Performance',
                            missingLabel: 'Missing Display Days',

                            overallCompareExpectedVsActualTitle: 'Expected vs Total Display Days',
                            overallCompareByPerformanceTitle: 'By Performance %',

                            overallTabTotalTabExpectedTitle: 'Target Display Days',
                            overallTabTotalTabActualTitle: 'Total Display Days',
                            overallTabTotalValueLabel: '{{count}} Display Days',
                            overallTabTotalPerformance: 'Display Days',
                            overallTabTotalPerformanceTime: 'Time',

                            mapSettingActualHeat: 'Display Days',
                            mapSettingExpectedHeat: 'Expected Display Days',
                        },
                        [PCAReportMetric.Frames]: {
                            expectedLabel: 'Expected $t(CommonWords.Frame)s',
                            actualLabel: 'Total $t(CommonWords.Frame)s',
                            percentageLabel: '$t(CommonWords.Frame)s Percentage',
                            performanceLabel: '$t(CommonWords.Frame)s Performance',
                            missingLabel: 'Missing $t(CommonWords.Frame)s',

                            overallCompareExpectedVsActualTitle:
                                'Expected vs Total $t(CommonWords.Frame)s',
                            overallCompareByPerformanceTitle: 'By Performance %',

                            overallTabTotalTabExpectedTitle: 'Target $t(CommonWords.Frame)s',
                            overallTabTotalTabActualTitle: 'Total $t(CommonWords.Frame)s',
                            overallTabTotalValueLabel: '{{count}} $t(CommonWords.Frame)s',
                            overallTabTotalPerformance: '$t(CommonWords.Frame)s',
                            overallTabTotalPerformanceTime: 'Time',

                            mapSettingActualHeat: '$t(CommonWords.Frame)s',
                            mapSettingExpectedHeat: 'Expected $t(CommonWords.Frame)s',
                        },
                        [PCAReportMetric.Plays]: {
                            expectedLabel: 'Expected Plays',
                            actualLabel: 'Total Plays',
                            percentageLabel: 'Plays Percentage',
                            performanceLabel: 'Plays Performance',
                            missingLabel: 'Missing Plays',

                            overallCompareExpectedVsActualTitle: 'Expected vs Total Plays',
                            overallCompareByPerformanceTitle: 'By Performance %',

                            overallTabTotalTabExpectedTitle: 'Target Plays',
                            overallTabTotalTabActualTitle: 'Total Plays',
                            overallTabTotalValueLabel: '{{count}} Plays',
                            overallTabTotalPerformance: 'Plays',
                            overallTabTotalPerformanceTime: 'Time',

                            mapSettingActualHeat: 'Plays',
                            mapSettingExpectedHeat: 'Expected Plays',
                        },
                        [PCAReportMetric.Impacts]: {
                            expectedLabel: 'Expected $t(CommonWords.Impacts)',
                            actualLabel: 'Total $t(CommonWords.Impacts)',
                            percentageLabel: '$t(CommonWords.Impacts) Percentage',
                            performanceLabel: '$t(CommonWords.Impacts) Performance',
                            missingLabel: 'Missing $t(CommonWords.Impacts)',

                            overallCompareExpectedVsActualTitle:
                                'Expected vs Total $t(CommonWords.Impacts)',
                            overallCompareByPerformanceTitle: 'By Performance %',

                            overallTabTotalTabExpectedTitle: 'Target $t(CommonWords.Impacts)',
                            overallTabTotalTabActualTitle: 'Total $t(CommonWords.Impacts)',
                            overallTabTotalValueLabel: '{{count}} $t(CommonWords.Impacts)',
                            overallTabTotalPerformance: '$t(CommonWords.Impacts)',

                            mapSettingActualHeat: '$t(CommonWords.Impacts)',
                            mapSettingExpectedHeat: 'Expected $t(CommonWords.Impacts)',
                        },
                        [PCAReportMetric.Time]: {
                            expectedLabel: 'Expected Time Played',
                            actualLabel: 'Total Time Played',
                            percentageLabel: 'Time Played Percentage',
                            performanceLabel: 'Time Played Performance',
                            missingLabel: 'Missing Time Played',

                            overallCompareExpectedVsActualTitle: 'Expected vs Total Time Played',
                            overallCompareByPerformanceTitle: 'By Performance %',

                            overallTabTotalTabExpectedTitle: 'Time Target',
                            overallTabTotalTabActualTitle: 'Total Time Played',
                            overallTabTotalValueLabel: '{{count}} Time Played',
                            overallTabTotalPerformance: 'Time Played',

                            mapSettingActualHeat: 'Time',
                            mapSettingExpectedHeat: 'Expected Time',
                        },

                        energyUsage: {
                            playsHeader: 'Plays',
                            playTimeHeader: 'Plays Time',
                            kwhHeader: 'kWh',
                            kgCo2eHeader: 'kg CO2e',
                        },

                        map: {
                            markersToggle: 'Screens',
                            hideHeatmap: 'Hide Heatmap',
                            noHeatmapSelected: 'Select Heatmap',
                            heatmapPerformance: '% Performance',
                            heatmapInSchedule: 'In Schedule',
                            heatmapOutSchedule: 'Out Schedule',
                            heatmapUnbooked: 'Unbooked',
                        },
                        fields: {
                            [PCAReportField.Town]: '$t(CommonWords.Town)',
                            [PCAReportField.TvRegion]: '$t(CommonWords.TVRegion)',
                            [PCAReportField.Conurbation]: '$t(CommonWords.Conurbation)',
                            [PCAReportField.Format]: 'Format',
                            [PCAReportField.MediaOwner]: '$t(CommonWords.MediaOwner)',
                            [PCAReportField.LineItemName]: 'Line Item',
                            [PCAReportField.SurfaceArea]: 'Surface Area',
                            [PCAReportField.Resolution]: 'Resolution (W x H)',
                            [PCAReportField.CreativeName]: 'Creative Name',
                            [PCAReportField.FrameId]: '$t(CommonWords.Frame) ID',
                            [PCAReportField.UnitId]: 'Unit ID',
                            [PCAReportField.DayOfWeek]: 'Day of Week',

                            // elastic search
                            [EsPCAReportField.Audience]: 'Audience',
                            [EsPCAReportField.AudienceSov]: 'Audience Sov',
                            [EsPCAReportField.CampaignId]: 'Campaign ID',
                            [EsPCAReportField.CarbonEmissionKgsCo2e]: 'kg CO2e',
                            [EsPCAReportField.CreativeId]: 'Creative ID',
                            [EsPCAReportField.CreativeName]: 'Creative Name',
                            [EsPCAReportField.Day]: 'Day #',
                            [EsPCAReportField.DisplayConurbation]: '$t(CommonWords.Conurbation)',
                            [EsPCAReportField.DisplayEnv]: 'Environment',
                            [EsPCAReportField.DisplayFileTypes]: 'File Types',
                            [EsPCAReportField.DisplayFormat]: 'Format',
                            [EsPCAReportField.DisplayId]: 'Display ID',
                            [EsPCAReportField.DisplayLat]: 'Display Lat',
                            [EsPCAReportField.DisplayLineItemId]: 'Line Item ID',
                            [EsPCAReportField.DisplayLineItemName]: 'Line Item Name',
                            [EsPCAReportField.DisplayLong]: 'Display Long',
                            [EsPCAReportField.DisplayOwner]: '$t(CommonWords.MediaOwner)',
                            [EsPCAReportField.DisplayOwnerFormat]:
                                '$t(CommonWords.MediaOwner) | Format',
                            [EsPCAReportField.DisplayResolution]: 'Resolution (W x H)',
                            [EsPCAReportField.DisplayScreenNameSummary]: 'Screen Name',
                            [EsPCAReportField.DisplaySlotDuration]: 'Display Slot Duration',
                            [EsPCAReportField.DisplaySlots]: 'Display Slots',
                            [EsPCAReportField.DisplaySov]: 'Display Sov',
                            [EsPCAReportField.DisplaySurfaceArea]: 'Surface Area',
                            [EsPCAReportField.DisplayTown]: '$t(CommonWords.Town)',
                            [EsPCAReportField.DisplayTvRegion]: '$t(CommonWords.TVRegion)',
                            [EsPCAReportField.DisplayType]: 'Display Type',
                            [EsPCAReportField.Dow]: 'Day Of Week',
                            [EsPCAReportField.Downtime]: 'Downtime',
                            [EsPCAReportField.EndDateTime]: 'End Date Time',
                            [EsPCAReportField.EndDate]: 'End Date',
                            [EsPCAReportField.EnergyUsesKwh]: 'Energy Usage kWh',
                            [EsPCAReportField.Environment]: 'Environment',
                            [EsPCAReportField.ExpectedImpacts]: 'Expected Impacts',
                            [EsPCAReportField.ExpectedImpactsInSchedule]:
                                'Expected Impacts In Schedule',
                            [EsPCAReportField.ExpectedPlays]: 'Expected Plays',
                            [EsPCAReportField.ExpectedPlaysInSchedule]:
                                'Expected Plays In Schedule',
                            [EsPCAReportField.ExpectedTime]: 'Expected Time',
                            [EsPCAReportField.ExpectedTimeInSchedule]: 'Expected Time In Schedule',
                            [EsPCAReportField.FrameId]: '$t(CommonWords.Frame) ID',
                            [EsPCAReportField.UnitId]: 'Unit ID',
                            [EsPCAReportField.Hour]: 'Hour of Day',
                            [EsPCAReportField.ImpactsReceived]: 'Impacts Received',
                            [EsPCAReportField.ImpactsReceivedInSchedule]:
                                'Impacts Received In Schedule',
                            [EsPCAReportField.ImpactsReceivedOutOfSchedule]:
                                'Impacts Received Out of Schedule',
                            [EsPCAReportField.ImpactsReceivedUnbooked]: 'Impacts Received Unbooked',
                            [EsPCAReportField.ImpactsWithVerification]: 'Impacts With Verification',
                            [EsPCAReportField.InsidePopDate]: 'Inside Pop Date',
                            [EsPCAReportField.IssueId]: 'Issue ID',
                            [EsPCAReportFilterField.AudienceVersion]: 'Audience Version',
                            [EsPCAReportField.NumberOfUniqueCreatives]: 'No. of Unique Creatives',
                            [EsPCAReportField.PlaysReceivedInSchedule]:
                                'Plays Received In Schedule',
                            [EsPCAReportField.PlaysReceivedOutOfSchedule]:
                                'Plays Received Out of Schedule',
                            [EsPCAReportFilterField.Schedule]: 'Scheduled',
                            [EsPCAReportField.PlaysReceivedUnbooked]: 'Plays Received Unbooked',
                            [EsPCAReportField.PlaysWithVerification]: 'Plays With Verification',
                            [EsPCAReportField.Schedule]: 'Schedule',
                            [EsPCAReportField.SlotDuration]: 'Slot Duration',
                            [EsPCAReportField.Sov]: 'Sov',
                            [EsPCAReportField.StartDateTime]: 'Start Date Time',
                            [EsPCAReportField.StartDate]: 'Date',
                            [EsPCAReportField.SumOfPlays]: 'Sum of Plays',
                            [EsPCAReportField.SumOfTime]: 'Sum of Time',
                            [EsPCAReportField.TimeReceivedInSchedule]: 'Time Received In Schedule',
                            [EsPCAReportField.TimeReceivedOutOfSchedule]:
                                'Time Received Out of Schedule',
                            [EsPCAReportField.TimeReceivedUnbooked]: 'Time Received Unbooked',
                            [EsPCAReportField.TimeWithVerification]: 'Time With Verification',
                        },
                        advancedFiltering: {
                            title: 'Advanced Filtering',
                            reset: 'Reset Filters',
                            search: 'Search',
                        },
                        scheduleDropdownPlaceholder: 'Schedule',
                        inScheduleLabel: 'In Schedule',
                        outScheduleLabel: 'Out Schedule',
                        unbookedLabel: 'Unbooked',
                        auditedImpacts: 'Audited $t(CommonWords.Impacts)',
                        cumulativeLabel: 'Cumulative',
                        dateLabel: 'Date',
                        hourLabel: 'Hour',
                        areaLabel: 'Area',
                        byHourTableTimeHeader: 'Time',
                        typeOverall: 'Overall',
                        typeOverTime: 'Over Time',
                        selectedTotalPlaceholder: '{{selected}}/{{total}} Selected',
                        overallLeaderboard: {
                            title: 'Leaderboards',
                            filterResults: 'Filter Results',
                        },
                        frameList: {
                            frameTabAll: 'All ({{count}})',
                            frameTabBooked: 'Booked ({{count}})',
                            frameTabUnbooked: 'Unbooked ({{count}})',
                            showingResults: 'Showing {{count}} of {{total}}',
                            addressHeader: 'Address',
                            closeDialogBtn: 'Close',
                            noHourData: '-',
                            hourColumn: 'Hour',
                            dayOverall: 'Overall',

                            timePlayedTotal: 'Played: {{duration}}',
                            timePlayedExpected: 'Expected: {{duration}}',
                            timePlayedOutOfSchedule: 'Out Of Schedule: {{duration}}',
                            timePlayedOutOFHours: 'Out Of Hours: {{duration}}',
                        },
                    },
                    Analytics: {
                        impressions: '$t(CommonWords.Impacts)',
                        plays: 'Plays',
                        timePlayed: 'Time Played',
                        totalImpressions: 'Total $t(CommonWords.Impacts)',
                        displayFormatLabel: 'Display Format',
                        dspImpressionsLabel: 'DSP $t(CommonWords.Impacts)',
                        geopathImpressionsLabel: 'Geopath $t(CommonWords.Impacts)',
                        geopathImpactsPercentageLabel: 'Geopath Impacts Percentage',
                        routeImpactsPercentageLabel: 'Route Impacts Percentage',
                        varianceLabel: 'Variance +/-',
                        performanceLabel: 'Performance',
                        displayDaysTitle: 'Display Days',
                        audienceAll: 'All audiences',
                        audienceNone: 'Non Audited',
                        audienceAverages: 'Averages',
                        comparison: 'Comparison',
                        againstAudienceLabel: 'Against {{value}}',
                        overall: 'Overall',
                        keyLabelImpressionsGeopath: 'DSP $t(CommonWords.Impacts) / Geopath',
                        keyLabelImpressionsRoute: 'DSP $t(CommonWords.Impacts) / Route',
                        keyLabelPlaysImpressions: 'Plays / $t(CommonWords.Impacts)',
                        keyLabelImpactsPlays: 'DSP $t(CommonWords.Impacts) / Plays',
                        keyLabelDisplayDaysImpressions:
                            'Display Days / DSP $t(CommonWords.Impacts)',
                        hourlyBreakdown: 'Hourly breakdown — {{value}}',
                        showPercent: 'Show %',
                        hidePercent: 'Hide %',
                        percentInfinity: '∞%',
                        impressionAnalysis: '$t(CommonWords.Impacts) Analysis',
                        playsTitle: 'Plays',
                        impactsTitle: '$t(CommonWords.Impacts)',
                        timeTitle: 'Time Played',
                        summaryTitleBy: 'by',
                        summaryTitleJoin: ' + ',
                        summaryTitleRunningTotal: 'Running Total',
                        summaryTitleHeatmap: 'Heatmap',
                        deliveryOfTarget: 'Delivery Of Target',
                        deliverySummary: 'Delivery Summary',
                        deliveryOverview: 'Delivery Overview',
                        deliveryOverviewOverall: 'Delivery Overview (Overall)',
                        deliveryOverviewToDate: 'Delivery Overview (To Date)',
                        allAudiencesDeliveryOverview: 'All Audiences Delivery Overview',
                        audienceDeliveryOverview: '{{value}} Delivery Overview',
                        averageFormatDeliveryOverview: 'Average Format Delivery Overview',
                        nonAuditedDeliveryOverview: 'Non-Audited Delivery Overview',
                        valueTitle: 'Value',
                        expectedValue: 'Expected Value',
                        actualValueDelivered: 'Actual Value Delivered',
                        expected: 'Expected',
                        and: 'and',
                        vs: 'vs',
                        variance: 'Variance',
                        varianceWithContext: 'Variance ({{value}})',
                        variancePercWithContext: '% Variance ({{value}})',
                        actual: 'Total',
                        average: 'Average',
                        both: 'Both',
                        audited: 'Audited',
                        auditedWithValue: 'Audited {{value}}',
                        performance: 'Performance',
                        inSchedule: 'In Schedule',
                        outSchedule: 'Out Schedule',
                        unbooked: 'Unbooked',
                        totalImpacts: 'Total $t(CommonWords.Impacts)',
                        totalDeliveredImpacts: 'Total Delivered $t(CommonWords.Impacts)',
                        expectedImpacts: 'Expected $t(CommonWords.Impacts)',
                        expectedImpactsToDate: 'Expected $t(CommonWords.Impacts) To Date',
                        expectedImpressionsToDate: 'Expected $t(CommonWords.Impacts) To Date',
                        totalCampaignExpectedImpacts:
                            'Total Campaign Expected $t(CommonWords.Impacts)',
                        totalTargetImpressions: 'Total Target $t(CommonWords.Impacts)',
                        impactsRemaining: '$t(CommonWords.Impacts) Remaining',
                        impactsPerformance: '$t(CommonWords.Impacts) Of Target',
                        percentageOfTargetAchieved: '% of Target Achieved',
                        percentageOfTotalTargetImpressions:
                            '% of Total Target $t(CommonWords.Impacts)',
                        percentageOfTargetImpressions: '% of Target $t(CommonWords.Impacts)',
                        percentageOfCampaignImpressionsAudited:
                            '% of Campaign $t(CommonWords.Impacts) Audited',
                        percentageOfCampaignImpressionsNonAudited:
                            '% of Campaign $t(CommonWords.Impacts) Non-Audited',
                        percentageOfTotalImpressions: '% of Total $t(CommonWords.Impacts)',
                        deliveredImpressions: 'Delivered $t(CommonWords.Impacts)',
                        deliveredDspImpressions: 'Delivered DSP $t(CommonWords.Impacts)',
                        deliveredAverageImpressions: 'Delivered Average $t(CommonWords.Impacts)',
                        deliveredNonAuditedImpressions:
                            'Delivered Non-Audited $t(CommonWords.Impacts)',
                        deliveredAudienceImpressions: 'Delivered {{value}} $t(CommonWords.Impacts)',
                        filteredImpressions: 'Filtered $t(CommonWords.Impacts)',
                        totalDeliveredImpressions: 'Total Delivered $t(CommonWords.Impacts)',
                        unfilteredImpressions: 'Unfiltered $t(CommonWords.Impacts)',
                        deliveryVariance: 'Delivery Variance',
                        dspVsAudienceVariancePerc: 'DSP vs {{value}} % Variance',
                        dspVsAudienceVariance: 'DSP vs {{value}} Variance',
                        dspVsAverageVariancePerc: 'DSP vs Average % Variance',
                        dspVsAverageVariance: 'DSP vs Average Variance',
                        campaignDays: 'Campaign Days',
                        numberOfDaysLive: 'Number Of Days Live',
                        totalPlays: 'Total Plays',
                        totalTime: 'Total Time Played',
                        variancePerc: '% Variance',
                        dspImpacts: 'DSP $t(CommonWords.Impacts)',
                        auditedImpacts: 'Audited $t(CommonWords.Impacts)',
                        dspAverageImpacts: 'DSP Average $t(CommonWords.Impacts)',
                        nonAuditedImpacts: 'Non Audited $t(CommonWords.Impacts)',
                        nonAuditedImpactsPercentage: '% Non Audited $t(CommonWords.Impacts)',
                        averageImpactsMultiplier: 'Average $t(CommonWords.Impacts) Multiplier',
                        averageDspImpressionMultiplierInfoAudited:
                            'Audited impressions and plays only',
                        averageDspImpressionMultiplierInfoAll: 'All impressions and plays',
                        averageDspImpressionMultiplier:
                            'Average DSP $t(CommonWords.Impacts) Multiplier',
                        averageNonAuditedImpressionMultiplier:
                            'Average Non-Audited $t(CommonWords.Impacts) Multiplier',
                        averageTotalImpactsMultiplier:
                            'Average Total $t(CommonWords.Impacts) Multiplier',
                        averageAuditedImpactsMultiplier:
                            'Average Audited $t(CommonWords.Impacts) Multiplier',
                        averageAudienceImpressionMultiplier:
                            'Average {{value}} $t(CommonWords.Impacts) Multiplier',
                        dspAverageImpactsMultiplier:
                            'Average DSP Average $t(CommonWords.Impacts) Multiplier',
                        totalTarget: 'Total Target',
                        expectedToDate: 'Expected To Date',
                        audienceImpressions: '{{value}} $t(CommonWords.Impacts)',
                        averageFormatImpressions: 'Average Format $t(CommonWords.Impacts)',
                        unitsLabel: 'Units',
                        cardTooltips: {
                            overall:
                                '% of the full-campaign target $t(CommonWords.Impacts) delivered across all displays. This card always reflects the full campaign, regardless of audience filters or other filters.',
                            toDateUnfiltered:
                                '% of the to-date target $t(CommonWords.Impacts) delivered. ≥100% means on or above target. View details for the full breakdown of delivered vs expected $t(CommonWords.Impacts) to date.',
                            toDateFiltered:
                                'Share of total campaign $t(CommonWords.Impacts) matched by the current filters. View details for the full breakdown including unfiltered $t(CommonWords.Impacts) and the unit count.',
                            audited:
                                'Share of total campaign $t(CommonWords.Impacts) that have audited {{value}} audience data. View details to compare DSP-reported $t(CommonWords.Impacts) with {{value}} audience $t(CommonWords.Impacts) and see the multipliers.',
                            average:
                                'Share of total campaign $t(CommonWords.Impacts) on displays without audited audience data — these use average format $t(CommonWords.Impacts) instead. View details to compare DSP-reported $t(CommonWords.Impacts) with average format $t(CommonWords.Impacts) and see the multipliers.',
                            nonAudited:
                                'Share of total campaign $t(CommonWords.Impacts) on displays without audited audience data. View details for the delivered non-audited $t(CommonWords.Impacts) and $t(CommonWords.Impacts) multipliers.',
                        },
                        reportTitles: {
                            totalImpacts: 'Running Total $t(CommonWords.Impacts)',
                            totalPlays: 'Running Total Plays',
                            impactsDayOfWeek: '$t(CommonWords.Impacts) by Day of Week',
                            impactsHour: '$t(CommonWords.Impacts) by Hour',
                            impactsHeatmap: '$t(CommonWords.Impacts) Heatmap',
                            impactsLineItem: '$t(CommonWords.Impacts) by Line Item',
                            impactsEnvironment: '$t(CommonWords.Impacts) by Environment',
                            impactsMediaOwnerFormat:
                                '$t(CommonWords.Impacts) by $t(CommonWords.MediaOwner) + Format',
                            impactsRegion: '$t(CommonWords.Impacts) by $t(CommonWords.Region)',
                            impactsMediaOwner:
                                '$t(CommonWords.Impacts) by $t(CommonWords.MediaOwner)',
                        },
                        conversionMeasurement: {
                            fields: {
                                eventName: 'Event Name',
                                state: 'State',
                                county: 'County',
                                town: 'Town',
                                zip: 'Zip',
                                mediaOwner: '$t(CommonWords.MediaOwner)',
                                displayEnv: 'Display Environment',
                                displayFormat: 'Display Format',
                                frame: '$t(CommonWords.Frame)',
                                screenName: 'Screen Name',
                                type: 'Type',
                            },
                        },
                    },
                    Creatives: {
                        uploadFile: 'Add Artwork',
                        supportedTypes: 'Supported files: JPG, PNG, GIF, MP4, MOV',
                    },
                    Costs: {
                        createdAtHeader: 'Created',
                        priceHeader: 'Price',
                        statusHeader: 'Status',
                        originalPriceHeader: 'Original Price',
                        displaysHeader: 'Display Count',
                        overrideReasonHeader: 'Override Reason',

                        statusActive: 'Active',
                        statusInactive: 'Inactive',

                        lineItemsDialogTitle: 'Displays',
                        dayCount: '{{count}} days',

                        overrideDialog: {
                            title: 'Override Price',
                            description:
                                "As an administrator you can override the campaign's price.",

                            priceLabel: 'New Price',
                            reasonLabel: 'Reason',
                            failedOverride: 'Failed to override price',
                        },
                    },
                },
                CombinedReport: {
                    title: 'Combined',
                    loadError: 'The combined report could not be loaded.',
                    refreshError: 'These figures could not be updated. Showing the last result.',
                    noData: 'No classic or digital data for this campaign yet.',
                    dataThrough: 'Data through {{date}}',
                    date: 'Date',
                    owner: '$t(CommonWords.MediaOwner)',
                    region: '$t(CommonWords.TVRegion)',
                    day: 'Day of Week',
                    hour: 'Hour',
                    impactsCombined: 'Combined $t(CommonWords.Impacts)',
                    impactsClassic: 'Classic $t(CommonWords.Impacts)',
                    impactsDigital: 'Digital $t(CommonWords.Impacts)',
                    displayType: 'Display type',
                    allTypes: 'Classic and digital',
                    classic: 'Classic',
                    digital: 'Digital',
                    expected: 'Expected $t(CommonWords.Impacts)',
                    // Named "<type> Expected" so the chart controls pair each with its own type.
                    expectedClassic: 'Classic Expected $t(CommonWords.Impacts)',
                    expectedDigital: 'Digital Expected $t(CommonWords.Impacts)',
                    total: 'Total $t(CommonWords.Impacts)',
                    runningTotal: 'Running Total $t(CommonWords.Impacts)',
                    byOwner: '$t(CommonWords.Impacts) by $t(CommonWords.MediaOwner)',
                    byRegion: '$t(CommonWords.Impacts) by $t(CommonWords.TVRegion)',
                    byDay: '$t(CommonWords.Impacts) by Day Of Week',
                    byHour: '$t(CommonWords.Impacts) by Hour of Day',
                    heatmap: '$t(CommonWords.Impacts) Heatmap',
                },
                ClassicPostings: {
                    title: 'Classic postings',
                    postingWindow: 'Posting window',
                    bookedDate: 'Booked date',
                    windowDay: '{{count}} day',
                    windowDays: '{{count}} days',
                    dataThrough: 'Data through {{date}}',
                    loadError: 'Classic postings could not be loaded.',
                    noClassicLines: 'No booked classic lines.',
                    Figures: {
                        delivered: 'Delivered to date',
                        deliveredSub: '$t(CommonWords.impacts) in schedule',
                        expected: 'Expected to date',
                        expectedSub: 'from booked start dates',
                        variance: 'Variance',
                        varianceSub: '{{pct}}% delivered',
                        unitsUp: '$t(CommonWords.Frame)s up',
                        unitsUpSub: '{{up}} of {{total}}',
                    },
                    Tiles: {
                        postersUp: 'Posters up',
                        postersUpOf: 'of {{total}}',
                        postersUpSub: 'booked classic lines',
                        pctOfUnits: '{{pct}}% of $t(CommonWords.frame)s',
                        outOfWindowSubOne: 'over {{count}} day',
                        outOfWindowSub: 'over {{count}} days',
                        viewScreens: 'View screens →',
                    },
                    Status: {
                        in: 'Within window',
                        out: 'Out of window',
                        wait: 'Awaiting data',
                    },
                    Filters: {
                        allLines: 'All {{count}} booked classic lines',
                        showing: 'Showing {{shown}} of {{total}} $t(CommonWords.frame)s',
                        reset: 'Reset filters',
                    },
                    Breakdown: {
                        byOwner: 'By $t(CommonWords.MediaOwner)',
                        byState: 'By $t(CommonWords.TVRegion)',
                        byMarket: 'By $t(CommonWords.Town)',
                        unit: '{{count}} $t(CommonWords.frame)',
                        units: '{{count}} $t(CommonWords.frame)s',
                        inWindow: '{{within}}/{{total}} in window',
                        avg: 'avg {{days}}d',
                        worst: 'worst {{days}}d',
                        awaiting: '{{count}} awaiting',
                    },
                    Table: {
                        title: 'Posting lines',
                        unit: '$t(CommonWords.Frame) / location',
                        owner: '$t(CommonWords.MediaOwner)',
                        market: '$t(CommonWords.Town)',
                        state: '$t(CommonWords.TVRegion)',
                        bookedStart: 'Booked start',
                        posterUp: 'Poster up',
                        daysLate: 'Days late',
                        impressions: '$t(CommonWords.Impacts)',
                        vsExpected: 'vs expected',
                        frame: '$t(CommonWords.Frame) {{frame}}',
                        awaiting: 'Awaiting',
                        onDate: 'on date',
                        late: '+{{days}}d',
                        empty: 'No $t(CommonWords.frame)s match these filters.',
                    },
                    Awaiting: {
                        title: 'Screens awaiting data',
                        screens: 'Screens',
                        owners: '$t(CommonWords.MediaOwners)',
                        longestWait: 'Longest wait',
                        waiting: 'Waiting',
                        days: '{{days}}d',
                        close: 'Close',
                        showInLines: 'Show in posting lines',
                    },
                },
                CampaignAnalytics: {
                    targetsHiddenForFilter:
                        "Targets are set per insertion order, so they can't be shown for this filter",
                },
                CampaignSchedule: {
                    dsp: {
                        insertionOrdersTab: 'Insertion Orders ({{count}})',
                        allowedVenuesTab: 'Allowed Venues ({{count}})',
                        noPermission:
                            'You do not have permission to view insertion order targeting.',

                        toolbar: {
                            selection: 'Insertion order selection',
                            editParameters: 'Edit table parameters',
                            uploadVenues: 'Upload allowed venues',
                            deleteVenues: 'Delete venue list',
                            sync: 'Sync IOs',
                            syncDone: 'Insertion orders re-resolved.',
                            download: 'Download',
                        },

                        filters: {
                            customer: 'Customer',
                            advertiser: 'Advertiser',
                            nameContains: 'Name contains',
                            schedule: 'Schedule',
                            any: 'Any',
                        },

                        columns: {
                            name: 'Name',
                            insertionOrderId: 'Insertion order ID',
                            customer: 'Customer',
                            advertiser: 'Advertiser',
                            minDate: 'Min date',
                            maxDate: 'Max date',
                            cpm: 'CPM',
                            impressionsTarget: '$t(CommonWords.Impacts) target',
                            attachedBy: 'Attached by',
                            schedule: 'Schedule',
                            clearAll: 'Clear All',
                        },

                        cpm: {
                            save: 'Save CPM',
                            invalid: 'CPM must be a number of zero or more.',
                            saved: 'CPM saved.',
                        },

                        placeholders: {
                            add: 'Add planned line',
                            label: 'Label',
                            awaiting: 'Awaiting insertion order',
                            link: 'Link insertion order',
                            linked: 'Insertion order linked',
                            delete: 'Delete planned line',
                            deleteConfirm: 'This removes its target from the campaign. Continue?',
                            alreadyHasTarget: 'This insertion order already has a target',
                        },

                        impressionsTarget: {
                            save: "Couldn't save the $t(CommonWords.impacts) target",
                            invalid:
                                'Enter a number of $t(CommonWords.impacts), with up to 2 decimal places',
                            saved: '$t(CommonWords.Impacts) target saved',
                        },

                        missingTarget:
                            'No $t(CommonWords.impacts) target: this insertion order adds nothing to Expected $t(CommonWords.Impacts)',
                        targetCount_one: '{{withTarget}} of {{count}} insertion order has a target',
                        targetCount_other:
                            '{{withTarget}} of {{count}} insertion orders have a target',

                        schedule: {
                            defaultLabel: 'Default — campaign dates, 24/7',
                            noDates: 'No schedule — the campaign has no dates',
                            applied: 'Schedule applied to {{count}} insertion orders.',
                            removed: 'Returned {{count}} insertion orders to the Default schedule.',
                        },

                        selection: {
                            title: 'Insertion Order Selection',
                            customer: 'Customer',
                            dateRange: 'Date range',
                            dateRangeHelper:
                                'Campaign dates plus two weeks either side, matched against first and last playout.',
                            searchOn: 'Search on',
                            search: 'Search',
                            apply: 'Apply',
                            catalogueOnly:
                                'Only insertion orders with playout data can be attached.',
                            attachedByRule:
                                'Attached by a table parameter — remove it in Edit table parameters.',
                            skipped: '{{count}} were already picked and were skipped.',
                        },

                        parameters: {
                            title: 'Edit Table Parameters',
                            tabInsertionOrderId: 'Insertion order ID',
                            tabInsertionOrderName: 'Insertion order name',
                            tabAdvertiser: 'Advertiser',
                            searchType: 'Search type',
                            customer: 'Customer',
                            dateRange: 'Date range — applied to min and max date',
                            identifiers: 'Identifiers',
                            identifiersHelper:
                                'One per line. Lines in a tab are alternatives; filled tabs are combined with AND.',
                            apply: 'Apply changes',
                            preview_one: 'Creates {{count}} rule',
                            preview_other: 'Creates {{count}} rules',
                            deleteSelected: 'Delete selected',
                            noneSaved: 'No table parameters saved yet.',
                            skipped: 'Already saved, so skipped: {{messages}}',
                            columns: {
                                identifier: 'Identifier',
                                searchType: 'Search type',
                                dateRange: 'Date range',
                                count: 'IO count',
                                customer: 'Customer',
                                actions: 'Actions',
                            },
                            errors: {
                                nothing_entered: 'Enter at least one identifier.',
                                id_must_be_exact:
                                    'An insertion order ID can only be matched exactly. Use the name or advertiser tab for a partial match.',
                                identifier_too_long: 'Identifiers can be at most 255 characters.',
                                dates_reversed: 'The date range ends before it starts.',
                                several_multi_line_tabs:
                                    'Tabs are combined with AND. Only one tab can list several identifiers; the others need exactly one.',
                            },
                        },

                        venues: {
                            title: 'Upload allowed venues',
                            file: 'CSV file',
                            dropzone:
                                'Drop the media plan screen list here, or click to choose a CSV',
                            validating: 'Checking the file…',
                            rowsRead: 'Rows read',
                            derived: 'Public IDs derived from network + venue ID',
                            duplicates: 'Duplicates skipped',
                            rejected: 'Rejected — no network or venue ID',
                            replaces: 'Replaces the list uploaded {{date}} (previous kept)',
                            replacesNone: 'No list has been uploaded yet',
                            save: 'Save allowed venue list',
                            saved: 'Allowed venue list saved.',
                            nothingWritten:
                                'Nothing has been saved yet — review the summary, then save.',
                            rejectionLine: 'Line {{line}}: {{reason}}',
                            columns: {
                                publicId: 'Public ID',
                                networkId: 'Network ID',
                                partnerVenueId: 'Venue ID',
                                venueName: 'Venue name',
                                venueType: 'Venue type',
                            },
                            empty: 'No allowed venue list. Every screen is treated as booked until one is uploaded.',
                            delete: {
                                title: 'Delete allowed venue list',
                                warning:
                                    'With no allowed venue list, nothing can be flagged as unbooked: every screen that plays counts as booked, including screens that were never on the plan. Upload a new list to turn this back on.',
                                count: 'This removes {{count}} venues. The list is kept in the history and can be restored by uploading it again.',
                                cancel: 'Cancel',
                                confirm: 'Delete list',
                                done: 'Allowed venue list deleted. Every screen now counts as booked.',
                            },
                        },
                    },
                    metaTitle: 'Schedule',
                    scheduleTabs: {
                        schedule: 'Schedule',
                        audienceTab: {
                            title: '$t(CommonWords.Owner)',
                            audienceType: 'Audience Type',
                            owner: '$t(CommonWords.MediaOwner)',
                            lastPlayed: 'Last Played Date By',
                            lastPlayedOptions: {
                                default: 'Default',
                                media: 'Media',
                            },
                            selectVersion: 'Select Audience Version',
                            impressions_per_play: `$t(CommonWords.Impacts) Per Play`,
                            save: 'Save',
                        },
                        targets: {
                            tabTitle: 'Targets',
                            createTarget: 'Create Target',
                            updateTarget: 'Update Target',
                            deleteTarget: 'Delete Target',
                            plays: 'plays',
                            impacts: 'impacts',
                            attachTarget: 'Attach Target',
                            detachTarget: 'Detach Target',
                            target: 'Target',
                            multiplier: 'Multiplier',
                            multiplierSuccess: 'Pipeline task queued',
                            verification: {
                                name: 'Name is required',
                                type: 'Type is required',
                                target: 'Target is required',
                                targetErr: 'Target must be a number with up to 5 decimal places',
                                schedulesSelect: 'Select one or more schedules',
                                atLeastOneSelected: 'At least one schedule must be selected',
                            },
                        },
                    },
                    lineItems: 'Line Items',
                    lineItemsPop: 'Line Items (POP)',
                    displays: 'Line Item Displays',
                    inAPack: `In a $t(CommonWords.Pack)`,
                    unknownTabLabel: 'Unknown',
                    displaysTabLabel: 'Displays',
                    table: {
                        scheduleDialogTitle: 'Schedules',
                        idCol: 'ID',
                        nameCol: 'Name',
                        ownerCol: '$t(CommonWords.Owner)',
                        frameIdCol: '$t(CommonWords.Frame) ID',
                        scheduleCol: 'Schedule',
                        sovCol: 'Sov',
                        viewAllSchedules: 'View all',
                        openPack: 'Open $t(CommonWords.Pack) ({{count}})',
                        closePack: 'Close',
                        actionsCol: 'Actions',
                        clearAll: 'Clear All',
                        tvRegion: '$t(CommonWords.TVRegion)',
                        sovDialog: {
                            header: 'Set SOV',
                            save: 'Save',
                        },
                    },
                    unknownTab: {
                        syncUnknownDisplays: 'Sync Unknown Displays',
                        removeSelected: 'Remove Selected',
                        queued: 'Queued After Successful Resync',
                        table: {
                            id: 'ID',
                            frameId: '$t(CommonWords.Frame) ID',
                        },
                    },
                    displaysTable: {
                        status: 'Status',
                        id: 'ID',
                        frameId: '$t(CommonWords.Frame) ID',
                        mediaOwners: '$t(CommonWords.MediaOwners)',
                        packs: '$t(CommonWords.Pack)s',
                        address: 'Address',
                        size: 'Size',
                        tvRegion: '$t(CommonWords.TVRegion)',
                        env: 'Environment',
                        schedule: 'Schedule',
                        active: 'Active',
                        inactive: 'Inactive',
                        sov: 'Sov',
                        slotLength: 'Slot Length',
                        name: 'Name',
                        screens: 'Screens',
                    },
                    displaysTab: {
                        uploadIds: 'Upload IDs',
                        removeDisplaysOrFrames: 'Remove Displays or $t(CommonWords.Frame)s',
                        frameIds: '$t(CommonWords.Frame) IDs',
                        displayIds: 'Display IDs',
                        displaysIds: 'Displays IDs',
                        add: 'Add',
                        removeSelectedFramesOrDisplays: 'Remove Selected Frames or Displays',
                        textAreaPlaceholder: '$t(CommonWords.Frame) IDs (one per line)',
                        displayIdPlaceholder: 'Display IDs (one per line)',
                        remove: 'Remove',
                        uploads: 'Uploads',
                        download: 'Download',
                        success: 'The task is queued and should appear shortly',
                    },
                    unknownsTab: {
                        syncUnknownDisplays: 'Sync Unknown Displays',
                        removeSelected: 'Remove Selected',
                        downloadUnknownDisplays: 'Download Unknown Displays',
                        id: 'ID',
                        frameId: '$t(CommonWords.Frame) ID',
                    },
                    detachDialogTitle: 'Detach Schedules from {{name}}',
                    detachDialogButton: 'Detach',
                    upload: 'Schedule File Upload',
                    downloadTemplate: 'Download Template CSV',
                    uploadFile: 'Upload File',
                    override: 'Override',
                    popSettings: {
                        title: 'Pop Settings',
                        audienceVersionsLabel: 'Audience Versions',
                        audienceSourceFilterLabel: 'Sources',
                        audienceSourceFilterPlaceholder: 'All sources',
                        audienceSourceUnknown: 'Other',
                        showInactiveSources: 'Show inactive sources',
                        audienceVersionPrimary: 'Primary',
                        audienceVersionFallback: 'Fallback',
                        audienceVersionsHelp:
                            'The first version is the primary audience. $t(CommonWords.Frame)s with no audience in it use the next version, in order.',
                        audienceVersionUnavailable: 'Unavailable version (#{{id}})',
                        audienceVersionFirstRelease: 'First release {{date}}',
                        audienceVersionLastRevision: 'Last revision {{date}}',
                        audienceVersionMoveUp: 'Move up',
                        audienceVersionMoveDown: 'Move down',
                        audienceVersionRemove: 'Remove',
                        audienceVersionsLoadError: 'Could not load audience versions.',
                        creativesSearchLabel: 'Creatives Search',
                        creativesSearchDatesLabel: 'Between Dates',
                        logImportSource: 'Log Import Source',
                        uniqueCreativesHelper: '{{count}} unique creative names',
                        creativesLabel: 'Creatives',
                        energyEfficiencyLabel: 'Energy Efficiency',
                        displayVerificationEfficiencyLabel: 'Display Verification',
                        featureFlagInherit: 'Use buyer setting',
                        featureFlagOn: 'On',
                        featureFlagOff: 'Off',
                        featureFlagInheritHelper: 'The buyer has this {{buyerState}}.',
                        featureFlagOverrideWarning:
                            'This campaign overrides its buyer. Only this campaign is affected; the buyer and every other campaign under it are unchanged.',
                        impressionMultiplierLabel: 'Impression Multiplier',
                        refreshDateLabel: 'Refresh Date',
                        refreshTypeLabel: 'Refresh Type',
                        minDate: 'Min Date',
                        minTime: 'Time',
                        graphReportTypeLabel: 'Graph Report Type',
                        logSourceCampaignIdentifiersTitle: 'Log Source Campaign Identifiers',
                        logSourceCampaignIdentifierSourceLabel: 'Source',
                        logSourceCampaignIdentifierLabel: 'Identifier',
                        saveSuccess: 'Pop Settings Saved',
                        submitBtn: 'Save',
                        search: 'Search',
                        name: 'Media Name',
                        id: 'ID',
                        source: 'Source',
                        creativeRef: 'Creative Ref',
                        campaignName: 'Campaign Name',
                        campaignExtId: 'Campaign Extid',
                        mediaExtId: 'Media Extid',
                        campaignRef: 'Campaign Ref',
                        firstSeen: 'First Seen',
                        lastSeen: 'Last Seen',
                        parameters: 'Parameters',
                        mediaSelection: 'Media Selection',
                        clear: 'Clear',
                        standard: 'Standard',
                        now: 'Now',
                        custom: 'Custom',
                        twoDaysAgo: 'Two Days Ago',
                        BasedOnOwner: 'Based On Owner',
                        classicMedia: {
                            title: 'Classic Media',
                            textAreaTitle: 'Media Names (One Per Line)',
                            textAreaPlaceholder: 'Enter Media Names, One Per Line',
                            id: 'ID',
                            classicMediaNames: 'Classic Media Names',
                            actions: 'Actions',
                            deleteSelected: 'Delete Selected',
                            submit: 'Submit',
                        },
                    },
                    mediaTable: {
                        title: 'POP Media',
                        editParams: 'Edit Table Parameters',
                        exportCsv: 'Export Data To CSV',
                        copyAllMediaNames: 'Copy all media names',
                        mediaRef: 'Media Ref',
                        idSearch: 'ID Search',
                        search: 'Search',
                        clear: 'Clear',
                        filterByParameters: 'Filter by Parameter',
                        applyChanges: 'Apply Changes',
                        apply: 'Apply',
                        searchCreatives: 'Search Creatives',
                        syncMedia: 'Sync Media',
                        searchType: {
                            contains: 'Contains',
                            containsAll: 'Contains All',
                            exact: 'Exact',
                            startsWith: 'Starts With',
                            endsWith: 'Ends With',
                        },
                        paramsEditDialog: {
                            deleteSelected: 'Delete Selected',
                            mediaCount: 'Media Count',
                            searchType: 'Search Type',
                            actions: 'Actions',
                            noneFound: 'No parameters found for {{param}}.',
                            identifier: 'Identifier',
                        },
                        groups: {
                            createNewGroup: 'Create New Group',
                            addMediaToGroup: 'Add to {{name}} Group',
                            newGroupName: 'New Group Name',
                            additionMessage: 'The following media will be added to the group:',
                            confirm: 'Confirm',
                            deleteGroupTitle: 'Delete Group',
                            deleteGroupMessage:
                                'Delete {{name}}? Any media in this group will be removed from it first.',
                            deleteGroupConfirm: 'Delete Group',
                            editCriteriaTitle: 'Edit Group Criteria',
                            criteria: 'Criteria',
                            currentCriteria: 'Current Criteria',
                            configuredCriteria: 'Configured Criteria',
                            noCriteria: 'No criteria found.',
                            addCriteria: 'Add Criteria',
                            saveCriteria: 'Save Criteria',
                            removeFromGroups: 'Remove from Groups',
                            removeMediaFromGroups: 'Remove the following media from all Groups',
                            all: 'All',
                            ungrouped: 'Ungrouped',
                        },
                    },
                    ganttPopup: {
                        scheduleName: 'Schedule Name',
                        hours: 'Hours',
                        days: 'Days',
                        dateRange: 'Date Range',
                    },
                    filters: {
                        searchLabel: 'Search',
                        environmentDropdownLabel: 'Environment',
                        resolutionDropdownLabel: 'Resolution',
                        sizeDropdownLabel: 'Format',
                        ownerDropdownLabel: '$t(CommonWords.MediaOwner)',
                        frameIdFilter: '$t(CommonWords.Frame) ID filter',
                        scheduleFilter: 'Schedule Filter',
                        frameIds: '$t(CommonWords.Frame) IDs',
                        results: 'Results',
                    },
                    Schedules: {
                        scheduleName: 'Schedule {{count}}',
                        list: {
                            addScheduleBtn: 'Add Schedule',
                            sitelistUploadSuccess: 'Uploaded Site List',
                            templateDropdownPlaceholder: 'Search Templates',
                            manageTemplates: 'Manage Templates',
                            collapseAll: 'Collapse All',
                        },
                        deleteDialog: {
                            title: 'Are you sure you want to delete Schedule "{{name}}"',
                            description: 'This action cannot be undone.',
                            deleteBtn: 'Delete Schedule',
                        },
                        detachSchedules: 'Detach schedules from screens',
                        attachSchedules: 'Attach schedules to screens',
                        apply: 'Apply',
                    },
                    Schedulables: {
                        updateScheduleScreens: 'Update {{name}} Screens',
                        countLabel_one: '{{count}} screen',
                        countLabel_other: '{{count}} screens',
                        addSelectedToSchedule_one: 'Add {{count}} Selected Screen To Schedule',
                        addSelectedToSchedule_other: 'Add {{count}} Selected Screens To Schedule',
                        removeSelectedToSchedule_one:
                            'Remove {{count}} Selected Screen From Schedule',
                        removeSelectedToSchedule_other:
                            'Remove {{count}} Selected Screens From Schedule',

                        lineItemsListHeader: 'Line Items',
                        screenListHeader: 'Screens',

                        detailsDialog: {
                            title: '"{{name}}" Schedules',

                            currentSchedules: 'Current Schedules',
                            otherSchedules: 'Other Schedules',
                            saveBtn: 'Save',
                        },
                    },
                    summary: {
                        sov: 'SOV',
                        slotLength: 'Slot Length',
                    },
                    Form: {
                        newSchedule: 'New Schedule',
                        updateSchedule: 'Update {{name}}',
                        cloneSchedule: 'Clone {{name}}',
                        rulesHeader: 'Rules',
                        dateRangeLabel: 'Date Range',
                        campaignDurationShortcut: 'Campaign Duration',
                        campaignRuns: 'Campaign runs {{start}} – {{end}}',
                        ruleOutsideCampaign: 'This rule falls outside the campaign dates',
                        ruleEndBeforeStart: 'The end date must be on or after the start date',
                        scheduleNameLabel: 'Name',
                        startTimeLabel: 'Start Time',
                        endTimeLabel: 'End Time',
                        timeHeader: 'Time',
                        weekdaysHeader: 'Weekdays',
                        sovHeader: 'SOV',
                        slotLengthHeader: 'Slot Length',
                        slotLengthHelperText: 'Slot Length Must Be Greater Than 0',
                    },
                },
                DataStatusLineItems: {
                    metaTitle: 'Data Status Line Items',
                    impacts: '$t(CommonWords.Impacts)',
                    mediaOwner: '$t(CommonWords.MediaOwner)',
                    campaignId: 'Campaign ID',
                    campaignName: 'Campaign Name',
                    buyer: 'Team',
                    liveDates: 'Live Dates',
                    lineItemName: 'Line Item Name',
                    expectedPlays: 'Expected Plays',
                    expectedImpacts: 'Expected $t(CommonWords.Impacts)',
                    totalPlays: 'Total Plays',
                    totalImpacts: 'Total $t(CommonWords.Impacts)',
                    plays: 'Plays',
                    status: 'Status',
                    comments: 'Comments',
                    lastSynced: 'Last Synced At',
                    addComment: 'Add Comment',
                    statusUnderInvestigation: 'Under Investigation',
                    statusCompleted: 'Completed',
                    campaignStatus: 'Campaign Status',
                },
                DataStatus: {
                    metaTitle: 'Data Status',
                    description: 'Monitor the data status of campaigns',
                    campaignTypeFilter: {
                        label: 'Campaign type',
                        linear: 'Linear',
                        programmatic: 'Programmatic',
                        options: {
                            default: 'Default',
                        },
                    },
                    table: {
                        dataStatusCol: 'Data Status',

                        bookerCol: 'Booker',
                        campaignOwnerCol: 'Campaign $t(CommonWords.Owner)',
                        campaignAssigneeCol: 'Campaign Assignee',

                        awaitingDataCol: 'Missing Data',
                        inspectingDataCol: 'Data Query',
                        awaitingBookingPlanCol: 'Missing Booking Data',

                        noticesMediaOwnersTitle: '$t(CommonWords.MediaOwners)',
                        noticesLineItemsTitle: 'Line Items',

                        bookingSummaryCol: 'Booking Summary',
                        actionRequiredCol: 'Action Required',
                        gathererCol: 'Gatherer',
                        scheduler: 'Scheduler',
                        reviewer: 'Reviewer',
                        bookingSummaryValidated: 'Booking Summary Signed Off',

                        bookingSummaryFileCount_one: '{{count}} File',
                        bookingSummaryFileCount_other: '{{count}} Files',

                        reportingColOverall: 'Overall:',

                        lastContactedMoCol: 'Last Contacted Date MO',

                        signOffCol: 'Sign off',

                        signOffCheckboxLabel: 'Sign Off',
                        inflightReportSentCheckboxLabel: 'Inflight Report Sent',
                        pcaReportSentCheckboxLabel: 'PCA Report Sent',

                        highPriorityCol: 'High Importance',
                        notesCol: 'Notes',
                        noteFailedSave: 'Error Saving Note',
                        ragCol: 'RAG',
                        plannerAwareCol: 'Planner Aware',
                        reportingCol: 'Reporting',
                    },
                    contacts: {
                        saveButton: 'Save',
                        contactsDropDownLabel: 'Edit Contacts',
                        otherContactsLabel: 'Other contacts',
                        copyEmailsLabel: 'Copy all emails',
                    },
                    awaitingDataFilterLabel: 'Awaiting Data from Vendor',
                    inspectingDataFilterLabel: 'Data received in query with Vendor',
                    awaitingBookingPlanFilterLabel: 'Booking Detail Outstanding',
                    awaitingDataLabel: 'Missing Data',
                    inspectingDataLabel: 'Data Query',
                    awaitingBookingPlanLabel: 'Missing Booking Data',
                    resetDataStatusLabel: 'Reset Data Status',
                    awaitingDataLabelAlert: 'Awaiting Data from Vendor',
                    inspectingDataLabelAlert: 'Data received in query with Vendor',
                    awaitingBookingPlanLabelAlert: 'Booking Detail Outstanding – please contact ',
                    bookingPlanValidatedLabelAlert:
                        'Booking Detail Requiring Sign Off – please contact ',
                    orderlineErrorsLabelAlert: 'There are orderline errors.',
                    LastContactedMediaOwner: {
                        mediaOwnerCol: '$t(CommonWords.MediaOwner)',
                        percentageCol: 'Percentage',
                        lastContactedCol: 'Last contacted MO',
                        saveDate: 'Save',
                    },
                },
                Instructions: {
                    openDialogBtnlabel: 'Instructions',
                    dialogTitle: 'Instructions',
                    emptyInstructions: 'There are no comments or Instructions added yet',
                    newMessagePlaceholder: 'Add a comment...',
                    failedToLoad: 'Failed to load instructions',
                    failedToLoadRetry: 'Reload',
                },
                PopShots: {
                    table: {
                        nameCol: 'Image file name',
                        fileSizeCol: 'File size',
                        uploadedCol: 'Uploaded Date - Time',
                        displayCol: 'Allocated display',
                        actionsCol: 'Actions',
                        showProPopShots: 'Only Professional Images',
                        isPro: 'Pro',
                    },
                    upload: {
                        title: 'Upload Pop Images',
                        lineItemLabel: 'Select Display',
                        isProLabel: 'Professional Image?',
                    },
                    delete: {
                        title: 'Delete Pop Shot',
                        description:
                            'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
                    },
                },
            },
            CampaignError: {
                errorableType: {
                    [CampaignErrorableType.Creative]: {
                        errorTitles: {
                            [CampaignErrorType.NoMatchingLineItemResolutions]: 'Invalid Resolution',
                            [CampaignErrorType.CreativeMissingResolution]: 'Missing Resolution',
                            [CampaignErrorType.MissingAnalogueLineItem]: 'No Analogue Displays',
                            [CampaignErrorType.IncorrectImageAnimation]: 'No Image Displays',
                            [CampaignErrorType.IncorrectVideoAnimation]: 'No Video Displays',
                            [CampaignErrorType.MissingLineItemMimeType]: 'Invalid File Type',
                        },
                        expected: {
                            [CampaignErrorType.NoMatchingLineItemResolutions]:
                                'There are no displays with a resolution of {{expected}}.',
                        },
                        available: {
                            // [CampaignErrorType.NoMatchingLineItemResolutions]: 'example override for available description {{available}}.',
                        },
                    },
                    [CampaignErrorableType.LineItem]: {
                        errorTitles: {
                            [CampaignErrorType.NoMatchingResolution]:
                                "No creatives for this line item's resolution",
                            [CampaignErrorType.IncorrectAnimation]:
                                "No creatives for this line item's animation type",
                            [CampaignErrorType.IncorrectImageAnimation]:
                                'No image creatives for this line item',
                            [CampaignErrorType.IncorrectFileAnalogue]:
                                'No analogue creatives for this line item',
                        },
                    },
                    [CampaignErrorableType.Campaign]: {
                        errorTitles: {
                            [CampaignErrorType.NoMatchingResolution]:
                                'Missing Creative Resolutions',
                            [CampaignErrorType.MissingCreativeResolutions]:
                                'Creatives With No Resolution Information',
                            [CampaignErrorType.NoMatchingResolutionCreatives]:
                                'Creatives Missing Equivalent Line Item Resolutions',
                            [CampaignErrorType.NoMatchingImageCreatives]:
                                'Missing Line Items For Playing Image',
                            [CampaignErrorType.NoMatchingAnalogueCreatives]:
                                'Missing Line Items For Playing Analogue',
                            [CampaignErrorType.NoMatchingVideoCreatives]:
                                'Missing Line Items For Playing Video',
                        },
                        expected: {
                            // [CampaignErrorType.NoMatchingResolution]: '{{expected}}',
                        },
                    },
                },
                expectedTitle: 'Expected',
                availableTitle: 'Available',
            },
            CampaignDisplay: {
                List: {
                    table: {
                        idCol: 'ID',
                        nameCol: 'Name',
                        ownerCol: '$t(CommonWords.Owner)',
                        resolutionCol: 'Resolution (W x H)',
                        creativeCountCol: 'Creatives',
                        downloadCol: 'Download',
                    },
                },
            },
            Creative: {
                SearchPage: {
                    metaTitle: 'Search System Creatives',
                    description: 'Search creatives from the whole system',

                    filterExpandButtonLabel: 'Filter',
                    nameSearchLabel: 'Filter by Name',
                    campaignSearchLabel: 'Filter by Campaign',
                    agencySearchLabel: 'Filter by Agency',
                    resolutionSearchLabel: 'Filter by Resolution (W x H)',
                    creativeTypeSearchLabel: 'Filter by Type',

                    creativeCount: '{{count}} of {{total}}',

                    creativeTypes: {
                        group: 'Group',
                        file: 'File',
                        image: 'Image',
                        video: 'Video',
                    },
                },
                List: {
                    table: {
                        idCol: 'ID',
                        nameCol: 'Name',
                    },
                },
            },
            DisplayIssues: {
                details: {
                    caseRecordType: 'CASE RECORD TYPE',
                    initialDiagnosis: 'INITIAL DIAGNOSIS',
                    description: 'DESCRIPTION',
                    priority: 'PRIORITY',
                },
                table: {
                    displayNameCol: 'Display affected',
                    startDateCol: 'Start Date',
                    startTimeCol: 'Start Time',
                    endDateCol: 'End Date',
                    endTimeCol: 'End Time',
                    downtimeCol: 'Downtime',
                    incidentIdCol: 'Incident ID',
                },
            },
            CreativeManagement: {
                groups: {
                    addGroup: 'Add Group',
                    header: 'Groups',
                    autoGroupsLabel: 'Auto',
                    allArtwork: 'All Creatives',
                    allArtworkRemove: 'All Groups',
                    invalidGroup: 'Invalid',
                    ungroupedGroup: 'Ungrouped',
                    unassignedGroup: 'Unassigned',
                    newGroupPlaceholder: 'New Group',

                    autoGroups: {
                        dialogTitle: 'Auto Groups Generation',

                        groupNamesHeader: 'Group names',
                        creativesCountHeader: 'Creatives',
                        createGroupsLabel: 'Create Groups',

                        generateGroupsErrorTitle: 'Oh no!',
                        generateGroupsRetryLabel: 'Refresh',
                    },
                },
                dragAndDrop: {
                    dragCount_one: '{{count}} creative',
                    dragCount_other: '{{count}} creatives',
                    dragMoveFailedTitle: 'Failed to move creatives',
                },
                warnings: {
                    invalidCreatives: 'Invalid Creatives',
                    filterAllButtonLabel: 'FILTER ALL',
                    campaignNeedsValidating: 'Creatives Require Validation',
                    revalidateCampaignLabel: 'VALIDATE NOW',
                    groupWarningCount_other: '{{count}} creative warnings',
                    groupWarningCount_one: '{{count}} creative warning',
                    duplicateFileNameWarning:
                        'The following file:  {{file}} have duplicate file names would you like to update these files?',
                    duplicateFileYesButton: 'Yes',
                    duplicateFileNoButton: 'No',
                },
                creatives: {
                    filters: {
                        reset: 'Reset',
                        apply: 'ApplyFilters',
                        searchPlaceholder: 'Search',
                        resolutionsPlaceholder: 'Resolution (W x H)',
                        aspectRatiosPlaceholder: 'Aspect Ratio',
                        fileTypesPlaceholder: 'Type',
                        errorsPlaceholder: 'By Errors',
                        durationLabel: 'Duration',
                        liveDateLabel: 'Live Date',
                        endDateLabel: 'End Date',
                        dateAddedLabel: 'Date Added',
                        errorLabels: {
                            [CampaignErrorType.NoMatchingLineItemResolutions]: 'Invalid Resolution',
                            [CampaignErrorType.CreativeMissingResolution]: 'Missing Resolution',
                            [CampaignErrorType.MissingAnalogueLineItem]: 'No Analogue Displays',
                            [CampaignErrorType.IncorrectImageAnimation]: 'No Image Displays',
                            [CampaignErrorType.IncorrectVideoAnimation]: 'No Video Displays',
                            [CampaignErrorType.MissingLineItemMimeType]: 'Invalid File Type',
                        },
                    },
                    table: {
                        nameCol: 'File name',
                        groupCol: 'Group',
                        typeCol: 'Type',
                        durationCol: 'Duration',
                        resCol: 'Resolution (W x H)',
                        aspectRatioCol: 'Ratio',
                        fileSizeCol: 'File Size',
                        liveDataCol: 'Live date',
                        endDataCol: 'End date',
                        uploadDateCol: 'Date added',
                        assignedCol: 'Assigned Displays',
                        updatedAtCol: 'Updated At',
                        actionsCol: 'Actions',
                        groupNameCol: 'Group',
                        downloadAction: 'Download',
                        deleteAction: 'Delete',
                        deleteNoPermission: 'You do not have permission to delete this creative',
                        assignAction: 'Assign',
                        dropMessage: 'Drop files here to upload',
                        dropDialogTitle: 'Uploading Files',
                        dropDialogCloseBtn: 'Close',

                        copyBtnNoSelected: 'Select creatives to copy',
                        copyBtnDefault: 'Copy selected',
                        copyBtnCopied: 'Copied!',
                        copyBtnFailed: 'Failed copy',
                        noCreatives: 'No creatives',
                    },
                    groups: {
                        deleteGroup: {
                            dialogTitle: 'Delete "{{name}}"',
                            dialogDescription:
                                'You can choose whether to keep the creatives in the "All Creatives" group, or whether to delete the group\'s creatives too.',
                            alsoDeleteCreativesLabel: 'Also Delete Creatives?',
                            deleteBtnLabel: 'Delete',
                        },
                    },
                    controls: {
                        selectedCount: '{{count}} selected',
                        downloadBtnLabel: 'Download',
                        moveBtnLabel: 'Move to',
                        deleteBtnLabel: 'Delete',
                        uploadBtnLabel: 'Upload',
                        liveDateBtnLabel: 'Set Live Dates',
                        endDateBtnLabel: 'Set End Dates',
                        updateDateRangeTitle: 'Set Live And End Dates',

                        move: {
                            title: 'Move Selected Files',
                            allArtwork: 'All Creatives',
                            chooseGroupLabel: 'Choose New Groups',
                            submitLabel: 'Submit',
                        },
                        delete: {
                            title: 'Delete Selected Files',
                            description_one:
                                'Are you sure you want to delete the selected creative?\nThis action cannot be undone.',
                            description_other:
                                'Are you sure you want to delete the selected creatives?\nThis action cannot be undone.',
                            buttonLabel_one: 'Delete Creative',
                            buttonLabel_other: 'Delete Creatives',

                            noPermission:
                                'You do not have permission to delete the following creatives:',
                            noPermissionTwo:
                                'Would you like to delete the other selected creatives?',
                        },
                        upload: {
                            title: 'Upload Artwork',
                            dropzoneText: 'Drag & drop files or Browse',
                            browseBtnLabel: 'Browse',
                            cancel: 'Cancel',
                            closeAnyway: 'Close Anyway',
                            fileList: 'The following files are still uploading : ',
                            confirmation:
                                ' Are you sure you want to proceed? This will cancel the ongoing uploads.',
                            uploadsInProgress: 'Uploads in progress',
                        },
                        dates: {
                            updateDateTitle: 'Update Live And End Dates',
                            updateDateDescription:
                                'Set the live and end dates of all selected files in one go.',
                            submitLabel: 'Set Dates',
                            dateRangeLabel: 'Live And End Dates',
                        },
                        rename: {
                            title: 'Rename Creative',
                            description: 'Rename "{{name}}"',
                            nameLabel: 'New Name',
                            saveLabel: 'Rename',
                            previewDialogNameLabel: 'Name',
                            previewDialogSave: 'Save',
                        },
                    },
                    assignDialog: {
                        lineItemNameCol: 'Display',
                        mediaOwnerCol: '$t(CommonWords.MediaOwner)',
                        environmentCol: 'Environment',
                        sizeCol: 'Format',
                        resolutionsCol: 'Resolutions',
                        fileTypesCol: 'File Types',

                        dialogTitle: 'Force Assign',
                        dialogDescription:
                            'Please choose which displays to assign this creative to by checking the checkboxes below',
                        assignButtonLabel: 'assign selected',

                        lineItemResolutionMismatch: 'Resolution does not match',
                        lineItemAlreadyAssigned: 'Already Assigned',
                        failedToAssign:
                            'An error has occurred while trying to assign creatives to the selected displays',
                        failedToLoadAssignData: 'Failed to load',
                        failedToLoadAssignDataRetry: 'Reload',

                        searchLabel: 'Search',
                        mediaOwnerDropdownLabel: '$t(CommonWords.MediaOwner)',
                        environmentDropdownLabel: 'Environment',
                        sizeDropdownLabel: 'Format',
                        resolutionDropdownLabel: 'Resolution (WxH)',
                    },

                    unAssignDialog: {
                        dialogTitle: 'Force UnAssign',
                        dialogDescription:
                            'Please choose which displays to unassign this creative from by checking the checkboxes below',
                        unAssignButtonLabel: 'unassign selected',
                        failedToUnAssign:
                            'An error has occurred while trying to unassign creatives from the selected displays',
                    },
                },
            },
            MyProfile: {
                metaTitle: 'My Profile',
                description: 'My user profile',
                favouritesTitle: 'Admin Favourites',
                favouritesDescription: 'Pick which admin routes should appear in your favourites.',
                favouritesAvailableTitle: 'Available admin pages',
                favouritesMaxLabel: 'Maximum {{count}} favourites.',
                favouritesOrderTitle: 'Favourites order',
                favouritesOrderDescription: 'Drag items to reorder how favourites appear.',
                dropHereLabel: 'Drag items here to add to favourites.',
                removeFavouriteLabel: 'Remove',
                emptyFavouriteSlotLabel: 'Drop favourite {{index}} here',
                noMoreAvailableLabel: 'All admin pages are currently in favourites.',
                favouritesPoolHint: 'Drag a favourite back here to remove it.',
                saveFavouritesLabel: 'Save favourites',
                favouritesSavedLabel: 'Favourites updated.',
            },
        },
        Auth: {
            SessionExpiredDialog: {
                title: 'Your session has expired',
                description:
                    'Log in again to carry on where you left off. Anything you were working on is still here.',
            },
            User: {
                UserProfileCard: {
                    joined: 'Joined',
                    from: 'From:',
                },
                table: {
                    idCol: 'ID',
                    nameCol: 'Name',
                    emailCol: 'Email',
                    languageCol: 'Language',
                    buyerCol: 'Team',
                    agencyCol: 'Agency',
                    ownerCol: '$t(CommonWords.Owner)',
                    rolesCol: 'Roles',
                    buyerDialogTitle: 'Teams',
                    roleDialogTitle: 'Roles',
                    agencyDialogTitle: 'Creative Agencies',
                    ownerDialogTitle: '$t(CommonWords.Owners)',
                },
            },
        },
        PopDisplays: {
            adminHomeSectionTitle: 'Pop Displays',
            AdminHome: {
                metaTitle: 'Pop Displays Admin',
                description: 'Pop Displays Admin Dashboard',
                logsTitle: 'Log Files',
            },
            JobStatus: {
                metaTitle: 'Task Queue',
                description: 'Tasks queue list page',
                table: {
                    idCol: 'ID',
                    nameCol: 'Task Name',
                    createdCol: 'Created',
                    durationCol: 'Duration',
                    statusCol: 'Status',
                    notesCol: 'Notes',
                    stopTaskLabel: 'Stop Task',
                    filterToSource: 'Filter to this {{type}}',
                    searchForSegment: 'Search for “{{term}}”',
                    openRecord: 'Open this {{type}} in a new tab',
                },
                state: {
                    queued: 'Queued',
                    running: 'Running',
                    succeeded: 'Completed',
                    failed: 'Failed',
                    stuck: 'Stuck',
                },
                ecsState: {
                    starting: 'Starting',
                    online: 'Running',
                    stopping: 'Stopping',
                    offline: 'Offline',
                },
                memory: {
                    used: '{{value}}mb used',
                    available: '{{value}}mb available',
                },
                stopTask: {
                    title: 'Stop Task',
                    description: 'Are you sure you want to stop "{{name}}"?',
                    confirm: 'Stop Task',
                    cancel: 'Cancel',
                },
                filters: {
                    completion: 'Completion',
                    notCompleted: 'Not yet completed',
                    completed: 'Completed',
                    sourceId: 'Source ID',
                    typedSourceId: '{{type}} ID',
                },
            },
            LogFileImport: {
                metaTitle: 'Log File Ingestion Explorer',
                description: 'View a list of log files ingested by the system.',
                uploadRawLog: 'Upload Raw Log',
                uploadClassicLog: 'Upload Classic Log',
                downloadClassicTemplate: 'Download Classic Template CSV',
                uploadSuccess: 'Upload completed and queued',
                clearAction: 'Clear',
                reprocess: 'Reprocess',
                ignore: 'Ignore',
                delete: 'Delete',
                bulkActions: 'Bulk Actions',
                applyAction: 'Apply Action',
                downloadDateTimeTemplate: 'Download Date Time Template CSV',
                downloadDateAndHourTemplate: 'Download Date and Hour Template CSV',
                selectLogSource: 'Select a Log Source',
                dropzoneText: 'Drag & Drop CSV or Click Here to Upload',
                submit: 'Submit',
                table: {
                    idCol: 'ID',
                    typeCol: 'Type',
                    statusCol: 'Status',
                    processing: 'Processing',
                    dateCol: 'Date',
                    fileCol: 'File',
                    fileSizeCol: 'File Size',
                    createdCol: 'Created',
                    updatedCol: 'Updated',
                },
                details: {
                    resourcesSectionTitle: 'Resources',
                    parsedSectionTitle: 'Entries',
                    filterByTypeLabel: 'Type',
                    filterByNameLabel: 'Name',
                },
            },
            LogFileResource: {
                table: {
                    typeCol: 'Type',
                    indexCol: 'index',
                    playsCol: 'Plays',
                    nameCol: 'Name',
                    extIdCol: 'Ext ID',
                    revisionCol: 'Revision',
                    itemCol: 'Type',
                    idCol: 'ID',
                    referenceCol: 'Reference',
                    impactsCol: 'Impacts',
                    durationCol: 'Duration',
                },
            },
            LogImportSource: {
                metaTitle: 'Log Import Sources',
                description: 'Log Import Sources',
                create: {
                    newSourceButton: 'New Source',
                    nameLabel: 'Name',
                    marketNameLabel: 'Select Market',
                    parseLimitLabel: 'Parse Limit',
                    archiveDateLimitLabel: 'Archive Date Limit',
                    archiveDeleteFilesLabel: 'Archive Delete Files',
                    activeLabel: 'Active',
                },
                table: {
                    idCol: 'ID',
                    nameCol: 'Name',
                    dirNameCol: 'Directory Name',
                    marketCol: 'Market',
                    parseLimit: 'Parse Limit',
                    lastPlayedAt: 'Last Played',
                    archiveDeletedFilesCol: 'Archive Del. Files',
                    activeCol: 'Active',
                    activeToggleLabel: 'Active: {{name}}',
                    updatingLabel: 'Updating {{name}}',
                    ownersCol: 'Owners',
                },
            },
            PlayoutExport: {
                metaTitle: 'Playout Export',
                description: 'Playout Export',
                sourcesLabel: 'Sources',
                submitLabel: 'Generate Export',
                successDefault: 'Your CSV export request has been will be emailed to your inbox',
                errorDefault: 'Failed to request export',
            },
            VirtualCampaignBuilder: {
                metaTitle: 'Virtual Campaign Builder',
                description: 'Generate reports for virtual campaigns',
                form: {
                    startDateLabel: 'Start Date',
                    endDateLabel: 'End Date',
                    marketLabel: 'Market',
                    ownerLabel: '$t(CommonWords.MediaOwners)',
                    mediaItemsTextarea: 'Media Items',
                    submitBtnLabel: 'Find Campaign Results',
                },
                summary: {
                    standardReport: 'Generate Report',
                    exportRerportSuccess: 'Export report successfully downloaded',
                    displaysReport: 'Generate Displays Report',
                    table: {
                        nameCol: 'Name',
                        channelCol: 'Channel',
                        frameCol: '$t(CommonWords.Frame)',
                        frameIdCol: '$t(CommonWords.Frame) Id',
                        slotsCol: 'Slots',
                        slotsDurationCol: 'Slots Duration',
                        ownerCol: '$t(CommonWords.MediaOwner)',
                        sourceCol: 'Source',
                        actualCol: 'Actual',
                        count: 'Count: {{count}}',
                        secs: 'Secs: {{count}}',
                        time: 'Time: {{count}}',
                        minDateCol: 'Min Date',
                        maxDateCol: 'Max Date',
                        playbackSummaryLabel: 'Playback Summary',
                        playbackSummaryDialogTitle: '"{{name}}" Playback Summary',
                    },
                },
            },
            Display: {
                metaTitle: 'Display Explorer',
                description: 'Displays List',
                deleteDisplay: 'Remove {{id}}',
                yes: 'Yes',
                no: 'No',
                table: {
                    idCol: 'ID',
                    sourceCol: 'Source',
                    nameCol: 'Name',
                    addressCol: 'Address',
                    channelCol: 'Channel',
                    frameCol: '$t(CommonWords.Frame)',
                    displayKeyCol: 'Key',
                    ownerCol: '$t(CommonWords.Owner)',
                    frameIdCol: '$t(CommonWords.Frame) ID',
                    displayPortalIdCol: 'Display Portal Id',
                    sizeCol: 'Format',
                    resolutionCol: 'Resolution',
                    slotsCol: 'Slots',
                    slotLengthCol: 'Slot Length',
                    envCol: 'Environment',
                    animationCol: 'Animation',
                    marketCol: 'Market',
                    deleteDisplayTitle: 'Delete Display',
                    deleteDisplayDescription:
                        'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
                    deleteButtonLabel: 'Delete Display',
                },
                newDisplay: {
                    title: 'New Display',
                    newDisplayButton: 'New Display',
                    createDisplayButton: 'Create Display',
                    sourceLabel: 'Source',
                    displayPortalLabel: 'Displays Portal Display ID',
                    displayPortalSearchLabel: 'Displays Portal Display Search',
                    channelLabel: 'Channel',
                    frameLabel: '$t(CommonWords.Frame)',
                    displayKeyLabel: 'Key',
                },
                editDisplay: {
                    title: 'Edit "{{name}}"',
                },
                Overview: {
                    tabs: {
                        details: 'Details',
                        dates: 'Date Breakdowns',
                    },
                },
            },
            ChannelFrame: {
                metaTitle: 'Channel Explorer',
                description: 'Channel $t(CommonWords.Frame) List',
                exportSuccess: 'Channel $t(CommonWords.Frame)s successfully downloaded',
                ids: "ID's",
                addDisplayPortalIds: 'Add Display Ids',
                submit: 'Submit',
                import: {
                    btnLabel: 'Upload',
                    title: 'Channel $t(CommonWords.Frame)s Upload',
                    description:
                        "Use this to map channel $t(CommonWords.frame)s to display portal's displays.\n\nUploaded file needs to be a .csv file and contain 'channel_frame_id' and 'display_id' columns.",
                },
                table: {
                    idCol: 'ID',
                    sourceCol: 'Source',
                    channelCol: 'Channel',
                    frameCol: '$t(CommonWords.Frame)',
                    frameExtIdCol: '$t(CommonWords.Frame) Ext Id',
                    playerExtIdCol: 'Player Ext Id',
                    uuidCol: 'Uuid',
                    playerCol: 'Player',
                    displayIdCol: 'Display Id',
                    displayPortalIdCol: 'Display Id',

                    removeFrameTitle: 'Remove "{{name}}" from display',
                    removeFrameDescription:
                        'Removing this $t(CommonWords.frame) will disassociate it from this display. It will not delete the $t(CommonWords.frame). This action cannot be undone.',
                    confirmRemoveFrame: 'Remove $t(CommonWords.Frame) From Display',

                    addFrameBtn: 'Add Channel $t(CommonWords.Frame)s',
                    addFrameTitle: 'Add Channel $t(CommonWords.Frame)s',
                    addFrameNoDisplayCheckbox: 'Show $t(CommonWords.Frame)s without displays',
                    addFrameDropdown: 'Channel $t(CommonWords.Frame)s',
                    actions: 'Actions',
                },
                details: {
                    displaysHeader: 'Displays',
                    dateBreakdownHeader: 'Date Breakdown',
                    mediaBreakdownHeader: 'Media Breakdown',
                    countLabel: 'Count',
                    durationLabel: 'Duration',
                    minDateLabel: 'Min Date',
                    maxDateLabel: 'Max Date',
                },
            },
            ChannelFrameMediaSummary: {
                table: {
                    mediaCol: 'Media',
                    durationCol: 'Duration',
                    countCol: 'Count',
                    minDateCol: 'Min Date',
                    maxDateCol: 'Max Date',
                    playbackSummaryBtn: 'Playback Summary',
                    playbackSummaryTitle: '"{{name}}" Playback Summary',
                },
            },
            ScheduleTemplates: {
                metaTitle: 'Scheduling Templates',
                description: 'Manage Scheduling Templates',
                table: {
                    idCol: 'ID',
                    nameCol: 'Name',
                    actionsCol: 'Actions',
                },
                schedules: {
                    add: 'Add Schedule',
                },
            },
        },
        Displays: {
            deAllocation: 'Deallocate Screens',
            tagRemoval: 'You Have Successfully Removed The Selected Screens From This Tag',
            packRemoval:
                "'You Have Successfully Removed The Selected Screens From This $t(CommonWords.Pack)",
            adminHomeSectionTitle: 'Displays',
            AdminHome: {
                metaTitle: 'Displays Admin',
                description: 'Displays Admin Dashboard',
            },
            manualAudience: {
                version: 'Audience Version',
                file: 'File',
                submit: 'Submit',
            },
            vendorIds: {
                dialogTitle: 'Are You Sure That You Want To Delete This Vendor',
                id: 'ID',
                name: 'Name',
                deleteVendorId: 'Delete',
                yes: 'Yes',
                no: 'No',
            },
            OperationalTimings: {
                id: 'ID',
                type: 'Type',
                day: 'Day',
                date: 'Date',
                startTime: 'Start Time',
                endTime: 'End Time',
                action: 'Action',
                recurring: 'Recurring',
                schedule: 'Schedule',
                monday: 'Monday',
                tuesday: 'Tuesday',
                wednesday: 'Wednesday',
                thursday: 'Thursday',
                friday: 'Friday',
                saturday: 'Saturday',
                sunday: 'Sunday',
                weekdays: 'Weekdays',
                weekends: 'Weekends',
                everyday: 'Everyday',
                chooseADay: 'Choose A Day',
                submit: 'Submit',
                selectOption: 'Select An Option',
                entries: 'Entries',
                del: 'Delete',
                entry: 'Entry',
            },
            DisplaysDisplayEdit: {
                metaTitle: 'Edit Display',
                description: 'Edit Display',
                submit: 'Submit',
                deleteDialog: {
                    title: 'Are you sure you want to delete this screen?',
                    yes: 'Yes',
                    no: 'No',
                },
                delete: 'Delete',
                deleteWithCampaign:
                    'You have attempted to remove a display that has a creative(s) assigned to it. Please un-assign the creative(s) from the display and try again',
                deleteWithResolution:
                    'You have attempted to remove a display that has a resolution(s) assigned to it. Please un-assign the resolution(s) from the display and try again',
                success: 'Display successfully edited',
                brochureWarning:
                    'The screen belongs to a $t(CommonWords.Pack). $t(CommonWords.Pack)s brochure supersedes.',
                brochureSuccess: 'Brochure Successfully Added',
                tabs: {
                    displayTab: 'Display',
                    operationalTimingsTab: 'Operational Timings',
                    tagTab: 'Tags',
                    emptyTagTab: 'No Tags exist in this display',
                    emptyOwnersTab: 'No $t(CommonWords.MediaOwners) exist on this display',
                    emptyPacksTab: 'No $t(CommonWords.Pack)s exist on this display',
                    ownersTab: '$t(CommonWords.Owners)',
                    packsTab: '$t(CommonWords.Pack)s',
                    vendorTab: 'Vendor IDs',
                    audience: 'Audience',
                    channelFramesTab: 'Channel $t(CommonWords.Frame)s',
                    dateBreakdownTab: 'Date Breakdown',
                },
                channelFrames: {
                    id: 'ID',
                    source: 'Source',
                    channel: 'Channel',
                    frame: '$t(CommonWords.Frame)',
                    uuid: 'UUID',
                    player: 'Player',
                    firstSeen: 'First Seen',
                    lastSeen: 'Last Seen',
                },
                audience: {
                    table: {
                        headers: {
                            versionId: 'Version Id',
                            versionName: 'Name',
                            releaseId: 'Release Id',
                            fallback: 'Fallback',
                            validDateFrom: 'Valid Date From V',
                            fDay: 'f Day',
                            fHour: 'f Hour',
                            audience: 'Audience',
                            sov: 'SOV',
                            uploadedBy: 'Uploaded By',
                        },
                        body: {
                            yes: 'Yes',
                            no: 'No',
                            NA: 'N/A',
                        },
                    },
                    filters: {
                        dateFrom: 'Date From',
                        days: 'Days',
                        hours: 'Hours From',
                        submit: 'Submit',
                    },
                },
                fields: {
                    type: 'Type',
                    resolutions: 'Resolutions',
                    latitude: 'Latitude',
                    longitude: 'Longitude',
                    town: '$t(CommonWords.Town)',
                    postCode: 'Post Code',
                    slots: 'Slots',
                    slotLen: 'Slot Length',
                    resolutionWidth: 'Width',
                    resolutionHeight: 'Height',
                    address: 'Address',
                    market: 'Market',
                    location: 'Location',
                    salesForce: 'Sales Force Display ID',
                    partner: 'Vendor',
                    tvRegion: '$t(CommonWords.TVRegion)',
                    conurbation: '$t(CommonWords.Conurbation)',
                    channel: 'Channel',
                    animation: 'Animation',
                    size: 'Size',
                    fileType: 'File Types',
                    environment: 'Environment',
                    sa: 'Surface Area',
                    frame: '$t(CommonWords.Frame)',
                    sov: 'SOV %',
                    energyConv: 'Energy Conversion Overload',
                    energyUsage: 'Energy Uses per Second',
                    link: 'Brochure Link',
                    broFile: 'Brochure File',
                    mediaOwners: '$t(CommonWords.Owner)',
                    frameId: '$t(CommonWords.Frame) ID',
                    display: 'Display',
                    logo: 'Logo',
                    status: 'Status',
                    active: 'Active',
                    inactive: 'Inactive',
                    prevFrameId: 'Previous $t(CommonWords.Frame) ID',
                    static: 'Static',
                    motion: 'Motion',
                    subtle: 'Subtle',
                    classic: 'Classic',
                    rotatingScrolling: 'Rotating Scrolling',
                    downloadButton: 'Download',
                    downloadButtonLabel: 'Download Brochure',
                    uploadBrochure: 'Upload Brochure',
                    closeDialog: 'Continue',
                },
            },
            DisplaysTagEdit: {
                metaTitle: 'Tags Edit',
                description: 'Tags Edit',
                submit: 'Submit',
                success: 'Tag successfully edited',
                deleteDialog: {
                    title: 'Are you sure you want to delete this tag?',
                    yes: 'Yes',
                    no: 'No',
                },
                delete: 'Delete',
                tabs: {
                    details: 'Details',
                    operationalTimingsTab: 'Operational Timings',
                    displaysWOT: 'Displays With Operational Timings',
                },
                fields: {
                    name: "Tag's Name",
                    operational: 'Operational',
                    description: 'Tag Description',
                    parent: 'Parent',
                    inactive: 'Inactive',
                    active: 'Active',
                    market: 'Market',
                    partner: 'Vendor',
                },
            },
            DisplaysOwnerEdit: {
                metaTitle: '$t(CommonWords.Owner) Edit',
                description: '$t(CommonWords.Owner) Edit',
                submit: 'Submit',
                success: '$t(CommonWords.Owner) successfully edited',
                noPacks:
                    'There are no $t(CommonWords.pack)s that belong to this $t(CommonWords.owner)',
                noDisplays: 'There are no displays that belong to this $t(CommonWords.owner)',
                deleteDialog: {
                    title: 'Are you sure you want to delete this $t(CommonWords.owner)?',
                    yes: 'Yes',
                    no: 'No',
                },
                delete: 'Delete',
                deletedOwner:
                    '$t(CommonWords.Owner) no longer exists. Please click below to return to the $t(CommonWords.Owners) list page.',
                ownersList: '$t(CommonWords.Owners)',
                tabs: {
                    editOwnerDetails: 'Edit $t(CommonWords.Owner) Details',
                    packs: '$t(CommonWords.Pack)s',
                    displays: 'Displays',
                    massUpdating: 'Mass Updating Displays',
                },
                selectLabels: {
                    empty: 'No status Filter',
                    active: 'Active',
                    inactive: 'Inactive',
                },
                fields: {
                    country: 'Country',
                    name: '$t(CommonWords.Owners) Name',
                    partner: "Vendor's Name",
                    address1: 'Address line 1',
                    address2: 'Address line 2',
                    market: 'Market',
                    address3: 'Address line 3',
                    address4: 'Address line 4',
                    postcode: 'Postcode',
                    extId: 'External ID',
                    vendor: 'Vendor Name',
                    enable: 'Enable',
                    disable: 'Disable',
                    popRefreshType: 'Pop Refresh Type',
                    days: 'Days',
                    logs: 'Logs',
                    time: 'Time (hh:mm)',
                    mins: 'Mins',
                    logSource: 'Log Source',
                    lastPlayedAt: 'Last Played At',
                },
            },
            DisplaysPackEdit: {
                metaTitle: '$t(CommonWords.Pack) Edit',
                description: '$t(CommonWords.Pack) Edit',
                submit: 'Submit',
                success: '$t(CommonWords.Pack) successfully edited',
                tabs: {
                    details: 'Details',
                    displayAllocation: 'Display Allocation',
                    opTimings: 'Operational Timings',
                },
                fields: {
                    resolutionWidth: 'Width',
                    resolutionHeight: 'Height',
                    market: 'Market',
                    owner: '$t(CommonWords.Owner)',
                    type: '$t(CommonWords.Pack) Type',
                    resolutions: 'Resolutions',
                    sov: 'SOV %',
                    size: 'Size',
                    animation: 'Animation',
                    slots: 'Slots',
                    slotLength: 'Slot Length',
                    env: 'Environment',
                    name: 'Name',
                    description: 'Description',
                    brochureLink: 'Brochure Link',
                    fileTypes: 'File Types',
                    updatedBy: 'Updated By',
                    uniliveId: 'UniLED ID',
                    pack: '$t(CommonWords.Pack)',
                    site: 'Site',
                    createdBy: 'Created By',
                    static: 'Static',
                    motion: 'Motion',
                    subtle: 'Subtle',
                    uploadBrochure: 'Upload Brochure',
                    replaceBrochure: 'Replace Brochure',
                    downloadButtonLabel: 'Download Brochure',
                    downloadButton: 'Download',
                    deleteButtonLabel: 'Delete Brochure',
                    deletePack: 'Are You Sure You Want To Delete This $t(CommonWords.Pack)',
                },
            },
            DisplaysList: {
                uploadManualAudience: 'Upload Manual Audience',
                downloadTemplate: 'Download Template',
                createButton: 'Create Screen',
                save: 'Save',
                metaTitle: 'Displays',
                description: 'Displays',
                downloadCSV: 'Download CSV',
                reImport: 'Re-import',
                allocation: 'Allocation',
                clearSelected: 'Clear Selected',
                massUpdating: 'Mass Update Displays',
                customFilters: {
                    frameIds: '$t(CommonWords.Frame) Ids',
                    displayIds: 'Display Ids',
                    frameCount: '{{count}} unique $t(CommonWords.frame)s',
                    displaysCount: '{{count}} unique displays',
                    toggle: 'toggle between display and $t(CommonWords.frame) ids',
                },
                allocationDialog: {
                    title: 'Screen Allocation',
                    allocationType: 'Allocation Type',
                    allocate: 'Allocate',
                    deallocate: 'Deallocate',
                    selected: 'Selected',
                    filtered: 'Filtered',
                    screensToAllocate: 'Screens To Allocate',
                    allocateTo: 'Allocate to',
                    packs: '$t(CommonWords.Pack)s',
                    tags: 'Tags',
                    empty: 'Please Select...',
                    submitBtn: 'Assign',
                    allowOverride: 'Allow  Override',
                    conflicts:
                        'Some of the screens are already assigned a different "{{name}}". Please Override or unselect them.',
                    selectAll: 'Select All Below',
                    deselectAll: 'Deselect All Below',
                    viewSelected: 'View Selected',
                    viewAll: 'View All',
                    override: 'Override',
                    back: 'Check selected',
                },
                table: {
                    allocation: 'Selected Screens',
                    idCol: 'ID',
                    addressCol: 'Address',
                    uIdCol: 'UID',
                    frameID: '$t(CommonWords.Frame) ID',
                    mediaOwners: '$t(CommonWords.Owner)',
                    conurbation: '$t(CommonWords.Conurbation)',
                    animation: 'Animation',
                    resolution: 'Resolution',
                    size: 'Size',
                    slot: 'Slot',
                    environment: 'Environment',
                    brochure: 'Brochure',
                    updateBrochure: 'Update Brochure',
                    deleteBrochure: 'Delete Brochure',
                    confirmation: 'Are you sure you want to delete this brochure',
                    deletedBrochure: 'Brochure has been deleted',
                    yes: 'Yes',
                    no: 'No',
                    deleteDisplay: 'Delete Display',
                    pack: '$t(CommonWords.Pack)',
                    tag: 'Tag',
                    status: 'Status',
                    editDisplay: 'Edit Display',
                    actions: 'Actions',
                },
            },
            OwnerList: {
                metaTitle: '$t(CommonWords.Owner)',
                description: '$t(CommonWords.Owner)',
                createBtn: 'Create $t(CommonWords.Owner)',
                submit: 'Submit',
                buttonForm: {
                    name: 'Name',
                    partner: "Vendor's Name",
                    markets: 'Markets',
                    countries: 'Countries',
                },
                table: {
                    id: 'ID',
                    partner: 'Vendor',
                    extId: 'External ID',
                    ownerId: '$t(CommonWords.Owner) ID',
                    name: 'Name',
                    uId: 'UID',
                    packs: '$t(CommonWords.Pack)s',
                    countries: 'Countries',
                    deleteOwner: 'Delete $t(CommonWords.Owner)',
                    displays: 'Displays',
                    market: 'Market',
                    actions: 'Enable/Disable $t(CommonWords.Owner)',
                    editOwner: 'Edit Owner',
                },
            },
            CountriesList: {
                metaTitle: 'Countries',
                description: 'Countries',
                createBtn: 'Create Country',
                submitBtn: 'Submit',
                table: {
                    id: 'ID',
                    extId: 'External ID',
                    name: 'Name',
                    alpha2: 'Alpha 2 Code',
                    alpha3: 'Alpha 3 Code',
                    numericCode: 'Numeric Code',
                    currency: 'Currency',
                },
            },
            DisplaysCountryEdit: {
                metaTitle: 'Countries Edit',
                description: 'Countries Edit',
                submitButton: 'Submit',
                success: 'Country successfully edited',
                fields: {
                    id: 'ID',
                    owners: '$t(CommonWords.Owners)',
                    status: 'Status',
                    name: 'Name',
                    alpha2: 'Alpha 2 Code',
                    alpha3: 'Alpha 3 Code',
                    numericCode: 'Numeric Code',
                    currency: 'Currency',
                    active: 'Active',
                    inactive: 'Inactive',
                },
            },
            MarketsList: {
                metaTitle: 'Markets',
                description: 'Markets',
                createBtn: 'Create Market',
                table: {
                    id: 'ID',
                    name: 'Name',
                    uId: 'UniLED ID',
                    country: 'Country',
                    currency: 'Currency',
                },
            },
            DisplaysMarketEdit: {
                metaTitle: 'Edit Market',
                description: 'Edit Market',
                submit: 'Submit',
                success: 'Market successfully edited',
                fields: {
                    country: 'Country',
                    name: 'Name',
                    owners: '$t(CommonWords.Owners)',
                    logo: 'Logo',
                    status: 'Status',
                    active: 'Active',
                    inactive: 'Inactive',
                },
            },
            Packs: {
                metaTitle: '$t(CommonWords.Pack)s',
                description: '$t(CommonWords.Pack)s',
                createBtn: 'Create $t(CommonWords.Pack)',
                submitBtn: 'Submit',
                table: {
                    id: 'ID',
                    resolutionCol: 'Resolutions',
                    uIdCol: 'UID',
                    extIdCol: 'External ID',
                    marketCol: 'Market ID',
                    nameCol: 'Name',
                    sizeCol: 'Size',
                    displaysCol: 'Displays',
                    brochure: 'Brochure',
                    deletePack: 'Delete $t(CommonWords.Pack)',
                    editPack: 'Edit $t(CommonWords.Pack)',
                    ownerName: '$t(CommonWords.Owner) Name',
                },
            },
            Analogue: {
                metaTitle: 'Analogues',
                description: 'Analogues',
                createBtn: 'Create Analogue Screen',
                submitBtn: 'Submit',
                table: {
                    id: 'ID',
                    extIdCol: 'External ID',
                    nameCol: 'Name',
                    marketCol: 'Market',
                    sizeCol: 'Size',
                    owner: '$t(CommonWords.Owner)',
                    resolution: 'Resolution',
                    description: 'Description',
                },
            },
            AnalgueEdit: {
                metaTitle: 'Analogue Edit',
                description: 'Analogue Edit',
                submitButton: 'Submit',
                success: 'Analogue successfully edited',
                fields: {
                    status: 'Status',
                    active: 'Active',
                    inactive: 'Inactive',
                    country: 'Country',
                    market: 'Market',
                    owner: '$t(CommonWords.Owner)',
                    description: 'Description',
                    name: 'Name',
                    size: 'Size',
                    resolution: 'Resolution',
                    env: 'Environment',
                },
            },
            Tags: {
                metaTitle: 'Tags',
                description: 'Tags',
                createBtn: 'Create Tag',
                submit: 'Submit',
                buttonForm: {
                    name: 'Name',
                    market: 'Market',
                    partner: 'Vendor',
                    parent: 'Parent',
                    desc: 'Description',
                    operational: 'Operational',
                    status: 'Status',
                    active: 'Active',
                    inactive: 'Inactive',
                    yes: 'Yes',
                    no: 'No',
                },
                table: {
                    id: 'ID',
                    extIdCol: 'External ID',
                    nameCol: 'Name',
                    operationalCol: 'Operational',
                    linkedCol: 'Linked',
                    displaysCol: 'Displays',
                    dwot: 'Displays Without Operational Times',
                    yes: 'Yes',
                    no: 'No',
                    deleteTag: 'Delete Tag',
                    editTag: 'Edit Tag',
                },
            },
        },
    },
    Component: {
        CopyButton: {
            copied: 'Copied!',
        },
        LoadingSpinner: {
            label: 'Loading',
        },
        LogFileParsedDaily: {
            groupByHourCheckbox: 'Hourly Breakdown',
            groupByMediaCheckbox: 'Group By Media Name',
            totalsRow: 'Totals',
            timePlayedTotal: 'Played: {{hours}} hours, {{minutes}} minutes, {{seconds}} seconds',
        },
        FileUploadDropzone: {
            invalidFilenames:
                "{{invalidCount}} file(s) were not added due to invalid characters in their names.\nInvalid characters are: '\\', '/', ':', '*', '?', '\"', '<', '>', '|', '~'",
        },
    },
    PasswordStrength: {
        minLength: 'Must be at least {{minLength}} characters long',
        match: 'Both passwords must match',
        number: 'Needs a number',
        case: 'At least 1 upper case & 1 lower case',
        specialChar: 'At least 1 or more special characters',
    },
    SubmitButton: {
        defaultLabel: 'Save',
        defaultDeleteLabel: 'Delete',
    },
    Dialog: {
        defaultCloseLabel: 'Close',
        defaultCancelLabel: 'Cancel',
        defaultSubmitLabel: 'Submit',
        defaultDeleteLabel: 'Delete',
        discardChanges: {
            title: 'Discard changes?',
            description: 'You have unsaved changes. If you close now, they will be lost.',
            keepEditing: 'Keep editing',
            discard: 'Discard',
        },
    },
    Pagination: {
        genericError: "We couldn't load this list. Please try again.",
        resetFilters: 'Reset',
        toggleFiltersLabel: 'Filters',
        searchPlaceholder: 'Search',
        emptyResults: 'No results',
        moreCount: '{{count}} More',
        moreDialogDefaultTitle: 'All',

        filterByCategoriesLabel: 'Filter by categories: {{filters}}',
        advancedFiltersLabel: 'Advanced Filters',
        exportNeedsFilterOrSearch: 'Set a filter or search first, to choose what this applies to',

        dateRange: {
            clear: 'Clear',
        },

        perPageLabel: 'Rows per page',
        skipToPageLabel: 'Go to page',
        loadingPageLabel: 'Loading page {{page}}',

        filterLabels: {
            Campaign: {
                'filter.buyer.id': 'Team',
                'filter.client.id': 'Brand',
                'filter.market.id': 'Market',
            },
            Displays_LineItem: {
                'filter.type': 'Type',
                'filter.display_type': 'Display Type',
                'filter.environment': 'Environment',
                'filter.animation': 'Animation',
                'filter.filetypes': 'File Types',
                'filter.fileTypes': 'File Types',
                'filter.resolution': 'Resolution (W x H)',
                'filter.size': 'Format',
                'filter.slot_length': 'Slot Length',
                'filter.slots': 'Slots',
                'filter.owner.id': '$t(CommonWords.Owner)',
                'filter.market.id': 'Market',
            },
            PopDisplays_JobStatus: {
                'filter.ecs_task_status': 'Task Status',
                'filter.statusable_type': 'Filter By',
                'filter.completed_at': 'Completion',
            },
        },
    },
    SystemSearch: {
        filters: 'Filters',
        recentPages: 'Recent Pages',
        recentPagesClearAll: 'Clear History',
        otherColumn: 'Recent Something Else',
        defaultSearchPlaceholder: 'System Search...',
        errorSearching: 'error searching',
        noResults: 'no results',
        impersonatingRecentPagesDisabled: '-- Recent Pages disabled while impersonating a user --',
        aliases: {
            dashboardPage: ['Dashboard Home', 'Home Page'],
            newCampaignPage: ['New Campaign', 'Create Campaign', 'Add Campaign'],
            dataStatusPage: ['Data Status', 'Campaign Data Status'],
            campaignListPage: ['Campaign List', 'Campaign Search'],
            agencyListPage: ['Search Creative Agencies', 'Creative Agency List'],
            buyerListPage: ['Search Teams', 'Team List'],
            clientListPage: ['Search Brands', 'Brand List'],
            ownerListPage: ['Search $t(CommonWords.Owners)', '$t(CommonWords.Owner) List'],
            creativesSearchPage: ['Search Creatives', 'Creatives List', 'Creatives Search'],
            adminHomePage: ['Admin', 'Admin Home'],
            skinListPage: ['Skins', 'Skins List'],
            marketListPage: ['Markets', 'Markets List'],
            roleListPage: ['Roles', 'Roles List'],
            userListPage: ['Users', 'Users List', 'User List', 'User Management'],
            myProfilePage: ['Profile', 'Update Password', 'My Profile', 'Change Password'],
            impersonatePage: ['Impersonate User', 'Help User', 'Impersonate'],
            frameSearchPage: ['$t(CommonWords.Frame) Search'],
            newUser: ['Create User', 'New User', 'Add User'],

            popDisplaysAdmin: ['Pop Displays Admin', 'Pop Displays Hone'],
            popDisplaysTaskQueue: ['Job Status', 'Task Queue', 'Task List'],
            popDisplaysLogFileImportList: [
                'Log File Injestion Explorer',
                'Log Files',
                'Log Files List',
                'Log File Import List',
            ],
            popDisplaysPlayoutExport: ['Playout Export'],
            popDisplaysVirtualCampaignBuilder: ['Virtual Campaign Builder'],
            popDisplaysLogImportSourceList: ['Log Import Sources', 'Log File Import Sources'],
            popDisplaysDisplayList: [
                'Pop Displays List',
                'Displays',
                'Displays List',
                'Display Explorer',
            ],
            popDisplaysChannelFrameList: [
                'Pop Channel $t(CommonWords.Frame)s List',
                'Channel $t(CommonWords.Frame)s',
                'Channel $t(CommonWords.Frame)s List',
                'Channel Explorer',
            ],
            popDisplaysScheduleTemplates: ['Schedule Templates'],

            displaysAdmin: ['Displays Admin', 'Displays Home'],
            displaysAdminDisplayList: ['Displays List', 'Displays Search'],
            displaysOwnerDisplayList: [
                '$t(CommonWords.Owner) List',
                '$t(CommonWords.Owner) Search',
            ],
            packsAdminDisplayList: ['$t(CommonWords.Pack)s List', '$t(CommonWords.Pack)s Search'],
            displaysAnalogueDisplayList: ['Analogues List', 'Analogues Search'],
            tagsDisplayList: ['Tags List', 'Tags Search'],
            displaysCountriesList: ['Countries List', 'Countries Search'],
            displaysMarketsList: ['Markets List', 'Markets Search'],
        },
        prefixes: {
            agency: 'ag',
            buyer: 'buy',
            owner: 'own',
            campaign: 'cam',
            skin: 'skin',
            dashboardConfig: 'dash',
            summaryConfig: 'sum',
            user: 'user',
            popdisplay: 'disp',
        },
        prefixDescriptions: {
            dashboardConfig: 'saved dashboard name',
            summaryConfig: 'saved summary name',
            agency: 'agency name',
            buyer: 'team name',
            owner: '$t(CommonWords.Owner) name',
            campaign: 'campaign name',
            skin: 'skin name',
            user: 'user name',
            popdisplay: 'display name',
        },
    },
    Meta: {
        pageTitlePrefix: 'UniLED | ',
        footCopyrightCompany: 'UniLED',
        footerPoweredBy: 'Powered by',
        newVersionAvailable:
            "We've released an update. Refresh the page to get the latest version.",
        newVersionRefresh: 'Refresh page',
    },
    Reporting: {
        noData: 'No Data',
        // A failed request, as opposed to one that came back with nothing (noData).
        loadFailed: "This report couldn't be loaded",
        retry: 'Retry',
        timeframes: {
            timeframeDaily: 'Daily',
            timeframeWeekly: 'Weekly',
            timeframeBiWeekly: 'Bi-Weekly',
            timeframeMonthly: 'Monthly',
            timeframeRange: 'Date Range',
            timeframeAll: 'Campaign Target',
            timeFrameAllLabel: 'All time',
            timeFrameAllCampaignDates: 'All Campaign Dates',
        },
        // Names for ReportDateSwitcher's icon-only buttons.
        dateSwitcher: {
            first: 'First period',
            previous: 'Previous period',
            next: 'Next period',
            last: 'Last period',
        },
        // Pluralised units for durationParts (c-lib): "24 minutes", "1 hour, 5 seconds".
        durationUnits: {
            hours_one: '{{count}} hour',
            hours_other: '{{count}} hours',
            minutes_one: '{{count}} minute',
            minutes_other: '{{count}} minutes',
            seconds_one: '{{count}} second',
            seconds_other: '{{count}} seconds',
        },
        ticks: {
            hoursLabel: '{{count}} hrs',
            minutesLabel: '{{count}} mins',
            secondsLabel: '{{count}} secs',
        },
        exporting: {
            exportSubmit: 'Export Image',
            exportSettings: 'Configuration',
            exportData: 'Export Data',
            exportSubmitLoading: 'Loading',

            form: {
                resetFormLabel: 'Reset Configuration',
                widthLabel: 'Width',
                heightLabel: 'Height',
                imageTypeLabel: 'Image Type',
                showLegendLabel: 'Show Legend',

                marginHeader: 'Margin',
                marginTopLabel: 'Top',
                marginRightLabel: 'Right',
                marginBottomLabel: 'Bot',
                marginLeftLabel: 'Left',

                xAxisHeader: 'X-Axis',
                xAxisAngleLabel: 'Text Angle (-360 -> 360)',
                xAxisAllLabels: 'All Labels',
                xAxisTextAnchor: 'Text Anchor',
                xAxisDateFormat: 'Date Format',
                xAxisDateFormatDocs: 'Format Docs',

                textAnchor: {
                    [ChartLabelAnchor.Start]: 'Start',
                    [ChartLabelAnchor.Middle]: 'Middle',
                    [ChartLabelAnchor.End]: 'End',
                },

                segmentsHeader: 'Segments',
                segmentsSortBy: 'Sort By',
                segmentsReverse: 'Reverse',
                segmentsResetOrder: 'Initial',
                segmentsHideAll: 'Hide All',
                segmentsShowAll: 'Show All',
                segmentsToggleHidden: 'Toggle Hidden',
                segmentSearchPlaceholder: 'Search...',
            },
        },
    },
    Duration: {
        hours_one: '{{count}} hr',
        hours_other: '{{count}} hrs',
        minutes_one: '{{count}} min',
        minutes_other: '{{count}} mins',
        seconds_one: '{{count}} sec',
        seconds_other: '{{count}} secs',
    },
    Weekdays: {
        /**
         * This is relied upon for ordering weekdays
         */
        1: 'Monday',
        2: 'Tuesday',
        3: 'Wednesday',
        4: 'Thursday',
        5: 'Friday',
        6: 'Saturday',
        7: 'Sunday',
    },
    WeekdaysShort: {
        1: 'Mon',
        2: 'Tue',
        3: 'Wed',
        4: 'Thu',
        5: 'Fri',
        6: 'Sat',
        7: 'Sun',
    },
    Calendar: {},
    Currency: {
        symbol: '£',
    },
    Forms: {
        autocomplete: {
            defaultOpenPlaceholder: 'Type to Search... (`esc` to close)',
            selectAllOptionLabel: 'Select All',
        },
        selectField: {
            clearLabel: 'Clear',
        },
        dateRangeField: {
            openPickerLabel: 'Open calendar',
        },
    },
    FileBrowser: {
        orderBy: 'Order By',
        orderByName: 'Name',
        orderByUpload: 'Upload Date',
        orderByAspectRatio: 'Aspect Ratio',
        filePreview: {
            openInTab: 'Open In New Tab',
            download: 'Download',
            expand: 'Expand',
            previewModelClose: 'Close',
            searchLabel: 'Search...',
            showCount: '{{count}} of {{total}}',
            resolutionDropdownLabel: 'Filter by resolution (W x H)',
            aspectRatioDropdownLabel: 'Filter by aspect ratio',
            fileTypeDropdownLabel: 'Filter by file type',
            fileDuration: 'Duration',
        },
    },
    Models: {
        Campaign: {
            status: {
                [CampaignStatus.CampaignStarted]: 'Campaign Started',
                [CampaignStatus.CreativesUploaded]: 'Creatives Uploaded',
                [CampaignStatus.CampaignReady]: 'Campaign Ready',
                [CampaignStatus.CampaignArchived]: 'Campaign Archived',
                [CampaignStatus.CampaignComplete]: 'Campaign Complete',
                [CampaignStatus.Unknown]: 'Unknown',
            },
            type: {
                linear: 'Linear',
                vistar_us: 'Vistar US',
                vistar_us2: 'Vistar US2',
                vistar_uk: 'Vistar UK',
                hivestack_us: 'Hivestack US',
                hivestack_uk: 'Hivestack UK',
                placeexchange_us: 'Place Exchange US',
            },
        },
        DisplaysPack: { status: { 0: '$t(CommonWords.Pack)', 1: 'Site' } },
        Displays_LineItem: {
            type: {
                [Displays_LineItemLineItemType.Digital]: 'Display',
                [Displays_LineItemLineItemType.Package]: '$t(CommonWords.Pack)',
                [Displays_LineItemLineItemType.SiteGroup]: 'Displays',
                [Displays_LineItemLineItemType.Analogue]: 'Classic',
                [Displays_LineItemLineItemType.Logo]: 'Logo Box',
            },
        },
        Alert: {
            startTime: 'Start: {{start}}',
        },
        PopDisplays_JobStatus: {
            // Filter options come back as the raw morph type, which is a fully qualified class
            // name. Anything unmapped falls through to the raw value.
            statusable_type: {
                'App\\Models\\Portal\\Campaign': 'Campaign',
                'App\\Models\\Displays\\Owner': '$t(CommonWords.Owner)',
                'App\\Models\\Displays\\Screen': 'Display',
                Displays: 'Display',
                Tags: 'Tag',
                Packs: '$t(CommonWords.Pack)',
            },
        },
    },
    DataLayer: {
        unknownError: 'Something went wrong. Please try again.',
    },
    BadRequestPage: {
        back: 'Back to home',
        status: {
            404: 'Page Not Found',
            400: 'Forbidden',
            403: 'Forbidden',
            500: 'You do not have access to this campaign, please contact support@uniledsoftware.com to request access.',
        },
    },
};

export default en;
